import { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { useProductGroupData } from './hooks/useProductGroupData';
import { useConfigurator } from './hooks/useConfigurator';
import { useAdminCatalogMatch } from './hooks/useAdminCatalogMatch';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowLeftRight, ArrowRight, ShoppingCart, Star, Wifi, CheckCircle,
  Zap, Ruler, Phone, Shield, BatteryCharging, Palette, MessageCircle,
  ChevronUp, ChevronDown, Sparkles, Download,
} from 'lucide-react';
import {
  getGroupById,
  getGroupedProducts,
  getRulonNavigatorUi,
  getConfiguratorStepCopy,
  addToCart,
  type RulonNavigatorUiCopy,
  addSystemV2ToCart,
  getCart,
  updateAdminGroupContent,
  deleteAdminCatalogSystemImage,
  resolveBackendMediaUrl,
  appendAdminCatalogSystemMedia,
  replaceAdminCatalogSystemMedia,
  deleteAdminCatalogSystemMediaItem,
  reorderAdminCatalogSystemMedia,
  setAdminCatalogSystemMediaPrimary,
  importAdminCatalogSystemMediaUrls,
  parseV2SystemMedia,
} from '../api';
import { useAppStore } from '../store';
import { canAccessAdminUi } from '../adminAccess';
import { getCategoryMeta } from '../categoryAssets';
import { useHaptic } from '../hooks/useHaptic';
import { bindAdminImageCrop } from '@/hooks/useAdminImageCrop';
import { downloadBackendMediaFile } from '../lib/downloadBackendMedia';
import { persistAdminProductGroupCopy } from '../lib/adminProductGroupCopySave';
import {
  mergeProductGroupCopyFromApi,
  resolveProductGroupFullCopy,
  resolveProductGroupShortCopy,
} from '../lib/productGroupCopy';
import {
  isRazdvizhnyeBranch,
  normalizeCatalogTierSlug,
  RAZDVIZHNYE_DEFAULT_TIER,
} from '../lib/razdvizhnyeTier';
import type { ProductGroupMediaSlide } from '../types';
import { ProductGroupHeroVideo } from '../components/ProductGroupHeroVideo';
import { SkeletonProductGroup } from '../components/SkeletonCard';
import { ProductLightboxPinchImage } from '../components/ProductLightboxPinchImage';
import { PROTOCOL_THEME } from '../protocolTheme';
import { protocolGroupLabel } from '../lib/controlCompatibility';
import {
  buildMergedStepCopySchema,
  parseStepCopyOverrides,
  type StepCopyOverrides,
} from '../lib/configuratorStepCopy';
import { ConfiguratorReveal } from '../components/configurator/ConfiguratorReveal';
import { ControlInfoSheet } from '../components/configurator/ControlInfoSheet';
import { RulonnyeTypeNavigator } from '../components/configurator/RulonnyeTypeNavigator';
import { PimSubtypeNavigator } from '../components/configurator/PimSubtypeNavigator';
import { SiblingGroupTileNavigator } from '../components/configurator/SiblingGroupTileNavigator';
import { resolveConfiguratorTileHint } from '../lib/configuratorTileHint';
import { ConfiguratorSizeCascade } from '../components/configurator/ConfiguratorSizeCascade';
import { sizeSelectionComplete } from '../lib/priceMatrixDimensions';
import type { MatrixDimKey } from '../lib/priceMatrixDimensions';
import { selectionFromGroupId, type RulonSelection } from '../lib/rulonnyeNavigator';
import {
  PIM_BRANCH_UI,
  bestPimSibling,
  defaultPimGroupId,
  findSiblingForSubtype,
  pimBranchKeyFromSlug,
  resolvePimGroupId,
  subtypeSlugFromGroupId,
  visiblePimNavTiles,
} from '../lib/pimBranchUi';
import { chipAccentStyle, chipStyleForPillLabel, colorAccentKey, tierAccentKey, tierPillLabel } from '../lib/configuratorAccent';
import {
  CFG_CHIP_COLOR,
  CFG_CHIP_OPENING_CENTER,
  CFG_CHIP_OPENING_GRID,
  CFG_CHIP_PRIMARY,
  CFG_CHIP_POWER_SOLO,
  CFG_CHIP_ROW,
  CFG_CHIP_TAG,
  CFG_CHIP_TIER,
  CFG_CHIP_TILE,
  CFG_CHIP_TRANSITION,
  CFG_SUMMARY_CHIP,
  cfgChipGridSpan,
  cfgChipSortActiveFirst,
  cfgChipSpanClass,
  mergeChipGridStyle,
} from '../lib/configuratorChip';
import { ConfiguratorChipGrid } from '../components/configurator/ConfiguratorChipGrid';
import { motion } from 'framer-motion';
import { getControlOptionInfo, WORKS_WITH_ECOSYSTEMS } from '../lib/controlOptionInfo';
import { isCatalogVideoFile, prepareCatalogMediaForUpload } from '../lib/catalogMediaUpload';
import { CATALOG_PDP_CROP_PRESETS } from '../lib/adminImageCrop';

const CATALOG_MEDIA_LIMIT = 10;

const CATALOG_UPLOAD_FAIL_MSG =
  'Не удалось загрузить файл. Нужен JPEG, PNG, WebP или MP4. С iPhone: Настройки → Камера → «Наиболее совместимые»; или выберите фото ещё раз — мы попробуем сконвертировать HEIC. На Android выберите снимок как фото с расширением .jpg/.png.';

type HeroSlide =
  | { kind: 'image'; src: string; key: string; mediaId?: number }
  | { kind: 'video'; src: string; posterUrl?: string; key: string; mediaId?: number };

/**
 * Слой слайда: готовность после смены src/ремонта StrictMode — только ref+onLoad недостаточно
 * (кэш: повторный onLoad не приходит; key с |src| давал лишние ремаунты при «плавающем» URL).
 */
function ProductGroupHeroSlideLayer(props: {
  kind: 'image' | 'video';
  src: string;
  posterUrl?: string;
  alt: string;
  /** Уникальный ключ DOM (слайд в карусели), чтобы key у img/video не совпал при двух одинаковых URL. */
  mediaDomKey: string;
  show: boolean;
  onDecodeError: () => void;
  stopClickBubble?: boolean;
  onVideoExpand?: (autoplay: boolean) => void;
  videoAutoPlay?: boolean;
}) {
  const { kind, src, posterUrl, alt, mediaDomKey, show, onDecodeError, stopClickBubble, onVideoExpand, videoAutoPlay } = props;
  const [mediaReady, setMediaReady] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useLayoutEffect(() => {
    if (!src.trim()) {
      setMediaReady(false);
      return;
    }
    setMediaReady(false);
    if (kind !== 'image') return;
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) setMediaReady(true);
  }, [src, kind]);

  useEffect(() => {
    if (kind !== 'image' || !src.trim()) return;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const img = imgRef.current;
      if (img?.complete && img.naturalWidth > 0) setMediaReady(true);
    };
    tick();
    const r1 = requestAnimationFrame(tick);
    const img = imgRef.current;
    if (img?.decode) {
      img.decode().then(() => { if (!cancelled) setMediaReady(true); }).catch(() => {});
    }
    return () => {
      cancelled = true;
      cancelAnimationFrame(r1);
    };
  }, [src, kind]);

  if (kind === 'video') {
    return (
      <ProductGroupHeroVideo
        key={`${mediaDomKey}|video`}
        src={src}
        posterUrl={posterUrl}
        show={show}
        onError={onDecodeError}
        stopClickBubble={stopClickBubble}
        onExpand={onVideoExpand}
        autoPlay={videoAutoPlay}
      />
    );
  }

  const visible = show && mediaReady;
  const layerClass = `w-full h-full object-contain absolute inset-0 transition-opacity duration-300 ${
    visible ? 'opacity-100 z-[2]' : 'opacity-0 pointer-events-none z-0'
  }`;

  return (
    <img
      key={`${mediaDomKey}|${src}`}
      ref={imgRef}
      src={src}
      alt={alt}
      loading="eager"
      decoding="async"
      referrerPolicy="no-referrer"
      className={layerClass}
      onLoad={() => setMediaReady(true)}
      onError={onDecodeError}
    />
  );
}

const IFACE_INFO: Record<string, { label: string; icon: string; color?: string }> = {
  smart:       { label: 'Умное', icon: '📶', color: '#4FC3F7' },
  battery:     { label: 'АКБ',   icon: '🔋', color: '#81C784' },
  phase:       { label: 'Фаза',  icon: '⚡', color: '#FFD54F' },
  phase_rs485: { label: 'F+485', icon: '🔌', color: '#90CAF9' },
  dry_contact: { label: 'Сухой', icon: '🔘', color: '#B0BEC5' },
  rs485:       { label: 'RS-485',icon: '🔌', color: '#80DEEA' },
};

const CTRL_ICONS: Record<string, string> = {
  'пульт': '📱', 'приложение': '📲', 'голос': '🎙️',
  'выключатель': '🔲', 'таймер': '⏱️', 'датчик': '📡', 'telegram': '✈️',
};

const SLIDING_CATEGORIES = ['Раздвижные электрокарнизы'];

/** Высота проёма (м) из блока «для замера» — в корзину, не в матрицу прайса. */
function parseClientHeightM(raw: string): number | null {
  const t = String(raw ?? '').trim().replace(',', '.');
  if (!t) return null;
  const h = Number(t);
  if (!Number.isFinite(h) || h <= 0 || h > 50) return null;
  return h;
}

/**
 * Не использовать list[0]: при поиске по подстроке попадает много систем.
 * Приоритет: категория, точное название, совпадение подстроки, media_count.
 */

/** То же подмножество полей, что `SystemPriceRequest` на бэке — один источник для price API и корзины v2. */
type CatalogV2SelectionBody = {
  interface_type?: string;
  protocol?: string;
  control_types?: string[];
  tier_slug?: string;
  profile_color?: string;
  motor_color?: string;
  color?: string;
  opening_type?: string;
  length_m?: number;
  width_m?: number;
  projection_m?: number;
  height_m?: number;
};

/** `dimensionValue` — точный замер клиента; округление вверх для прайса — на backend. */
function buildCatalogV2SelectionBody(args: {
  selectedIfaceType: string | null;
  selectedProtocol: string | null;
  selectedControlTags: string[];
  selectedTierSlug: string | null;
  selectedColor: string | null;
  selectedProfileColor: string | null;
  selectedMotorColor: string | null;
  selectedOpening: string | null;
  dimKey: MatrixDimKey | null;
  dimensionValue: number | null;
  secondDimKey?: MatrixDimKey | null;
  secondDimensionValue?: number | null;
}): CatalogV2SelectionBody {
  const {
    selectedIfaceType,
    selectedProtocol,
    selectedControlTags,
    selectedTierSlug,
    selectedColor,
    selectedProfileColor,
    selectedMotorColor,
    selectedOpening,
    dimKey,
    dimensionValue,
    secondDimKey,
    secondDimensionValue,
  } = args;
  const body: CatalogV2SelectionBody = {
    interface_type: selectedIfaceType ?? undefined,
    protocol: selectedProtocol ?? undefined,
    control_types: selectedControlTags.length > 0 ? selectedControlTags : undefined,
    tier_slug: selectedTierSlug ?? undefined,
    profile_color: selectedProfileColor ?? undefined,
    motor_color: selectedMotorColor ?? undefined,
    color: selectedColor ?? selectedProfileColor ?? undefined,
    opening_type: selectedOpening ?? undefined,
  };
  if (dimensionValue !== null && dimKey !== null) {
    if (dimKey === 'length_m') body.length_m = dimensionValue;
    else if (dimKey === 'width_m') body.width_m = dimensionValue;
    else if (dimKey === 'projection_m') body.projection_m = dimensionValue;
  }
  if (secondDimensionValue != null && secondDimKey) {
    if (secondDimKey === 'length_m') body.length_m = secondDimensionValue;
    else if (secondDimKey === 'width_m') body.width_m = secondDimensionValue;
    else if (secondDimKey === 'projection_m') body.projection_m = secondDimensionValue;
  }
  return body;
}

const OPENING_TYPES = [
  { val: 'left', label: 'Влево', icon: 'left' as const },
  { val: 'center', label: 'К центру', icon: 'center' as const },
  { val: 'right', label: 'Вправо', icon: 'right' as const },
];

function OpeningDirectionIcon({ kind }: { kind: 'left' | 'center' | 'right' }) {
  const cls = 'w-5 h-5 shrink-0';
  if (kind === 'left') return <ArrowLeft className={cls} strokeWidth={2.25} aria-hidden />;
  if (kind === 'right') return <ArrowRight className={cls} strokeWidth={2.25} aria-hidden />;
  return <ArrowLeftRight className={cls} strokeWidth={2.25} aria-hidden />;
}

export default function ProductGroupPage() {
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const navigate = useNavigate();
  const { token, discountPct, role, telegramId, authAdminHint } = useAppStore();
  const setCart = useAppStore((s) => s.setCart);
  const haptic = useHaptic();

  const goBack = () => {
    if (from === 'motors') { navigate('/catalog?motors=1'); return; }
    navigate('/catalog');
  };
  const toGroupUrl = (id: string, tier?: string | null) => {
    const params = new URLSearchParams();
    params.set('id', id);
    if (from) params.set('from', from);
    const tierNorm = normalizeCatalogTierSlug(tier ?? searchParams.get('tier'));
    if (tierNorm) params.set('tier', tierNorm);
    return `/product-group?${params.toString()}`;
  };

  const routeGroupId = searchParams.get('id');
  const catalogTier = useMemo(
    () => normalizeCatalogTierSlug(searchParams.get('tier')),
    [searchParams],
  );
  const resolvedRouteGroupId = useMemo(() => resolvePimGroupId(routeGroupId), [routeGroupId]);

  useLayoutEffect(() => {
    if (!routeGroupId || !resolvedRouteGroupId || resolvedRouteGroupId === routeGroupId) return;
    navigate(toGroupUrl(resolvedRouteGroupId), { replace: true });
  }, [routeGroupId, resolvedRouteGroupId, navigate, from]);

  const dataGroupId = resolvedRouteGroupId || routeGroupId;
  const SUPPORT_TG_URL = 'https://t.me/onvizmsk';
  const isAdmin = useMemo(
    () => canAccessAdminUi({ role, telegramId, serverAdminPanelHint: authAdminHint }),
    [role, telegramId, authAdminHint],
  );

  const [selectedSubtype, setSelectedSubtype] = useState<string | null>(null);
  const [subtypeLoading, setSubtypeLoading] = useState(false);
  const [slideIdx, setSlideIdx] = useState(0);
  const [failedSlides, setFailedSlides] = useState<Set<number>>(new Set());
  const [lightbox, setLightbox] = useState(false);
  const [lightboxZoomed, setLightboxZoomed] = useState(false);
  const [lightboxVideoAutoplay, setLightboxVideoAutoplay] = useState(false);
  const [carouselVideoAutoplay, setCarouselVideoAutoplay] = useState(false);
  const lightboxSlideIdxRef = useRef(slideIdx);

  const openVideoLightbox = useCallback(
    (autoplay: boolean) => {
      setCarouselVideoAutoplay(false);
      setLightboxVideoAutoplay(autoplay);
      setLightboxZoomed(false);
      lightboxSlideIdxRef.current = slideIdx;
      setLightbox(true);
      haptic.selection();
    },
    [haptic, slideIdx],
  );

  const collapseVideoLightbox = useCallback(() => {
    haptic.selection();
    setCarouselVideoAutoplay(true);
    setLightboxZoomed(false);
    setLightbox(false);
  }, [haptic]);

  useEffect(() => {
    if (!carouselVideoAutoplay) return;
    const t = window.setTimeout(() => setCarouselVideoAutoplay(false), 800);
    return () => window.clearTimeout(t);
  }, [carouselVideoAutoplay, slideIdx]);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [showFullSpecs, setShowFullSpecs] = useState(false);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [validationHint, setValidationHint] = useState<string | null>(null);
  const [controlInfoEntry, setControlInfoEntry] = useState<ReturnType<typeof getControlOptionInfo>>(null);
  const tierBlockRef = useRef<HTMLDivElement | null>(null);
  const controlBlockRef = useRef<HTMLDivElement | null>(null);
  const ifaceBlockRef = useRef<HTMLDivElement | null>(null);
  const protocolBlockRef = useRef<HTMLDivElement | null>(null);
  const colorBlockRef = useRef<HTMLDivElement | null>(null);
  const profileColorBlockRef = useRef<HTMLDivElement | null>(null);
  const motorColorBlockRef = useRef<HTMLDivElement | null>(null);
  const sizeBlockRef = useRef<HTMLDivElement | null>(null);
  const skipAutoFocusRef = useRef(true);
  const adminAppendMediaInputRef = useRef<HTMLInputElement | null>(null);
  const adminReplaceMediaInputRef = useRef<HTMLInputElement | null>(null);
  const [adminUploadingImage, setAdminUploadingImage] = useState(false);
  const [adminDeletingImage, setAdminDeletingImage] = useState(false);
  const [adminDownloadingMediaId, setAdminDownloadingMediaId] = useState<number | null>(null);
  const { cropModal: adminCropModal, openFromFile: openAdminCrop } = bindAdminImageCrop();
  const [adminLinkingCarousel, setAdminLinkingCarousel] = useState(false);
  const [rulonNav, setRulonNav] = useState<RulonSelection>({
    installType: null,
    series: null,
    fabric: null,
  });

  // ─── Hook: data loading ───────────────────────────────────────────────────
  const resetCfg = useCallback(() => {
    skipAutoFocusRef.current = true;
  }, []);
  const {
    group, loading, siblingGroups, catalogSubtypes, heroReady,
    setGroup,
  } = useProductGroupData(dataGroupId, resetCfg, catalogTier);

  const effectiveGroupId = useMemo(() => {
    const fromCard = group?.group_id != null && String(group.group_id).trim() !== '' ? String(group.group_id).trim() : '';
    return fromCard || routeGroupId || null;
  }, [group?.group_id, routeGroupId]);

  const groupShortCopy = useMemo(() => (group ? resolveProductGroupShortCopy(group) : ''), [group]);
  const groupFullCopy = useMemo(() => (group ? resolveProductGroupFullCopy(group) : ''), [group]);

  const systemSlug = effectiveGroupId?.startsWith('v2::') ? effectiveGroupId.replace(/^v2::/, '') : null;

  // V3 systems have plain slug as group_id (no prefix like v2:: or st::)
  const v3DirectSlug = effectiveGroupId && !effectiveGroupId.startsWith('v2::') && !effectiveGroupId.startsWith('st::')
    ? effectiveGroupId
    : null;

  // ─── Hook: admin catalog match + gallery ─────────────────────────────────
  const {
    adminSystemId, adminCatalogSlug, publicV2Slug, adminGalleryList, setAdminGalleryList,
    adminDescDraft, adminFullDescDraft, adminAdvantagesDraft,
    adminEditingDesc, adminEditingFullDesc, adminEditingAdvantages,
    adminSavingDesc, adminSavingFullDesc, adminSavingAdvantages,
    setAdminDescDraft, setAdminFullDescDraft, setAdminAdvantagesDraft,
    setAdminEditingDesc, setAdminEditingFullDesc, setAdminEditingAdvantages,
    setAdminSavingDesc, setAdminSavingFullDesc, setAdminSavingAdvantages,
    reloadAdminGallery,
  } = useAdminCatalogMatch(group, isAdmin, routeGroupId, systemSlug || v3DirectSlug, null, setGroup, catalogTier);

  const effectiveCatalogSlug =
    systemSlug ||
    v3DirectSlug ||
    adminCatalogSlug ||
    publicV2Slug ||
    (group?.catalog_v2_slug ? String(group.catalog_v2_slug).trim() : '') ||
    null;

  // ─── Hook: configurator state + pricing ──────────────────────────────────
  const {
    selectedTierSlug, selectedBattery, selectedIfaceType, selectedProtocol,
    selectedControlTags, selectedColor, selectedProfileColor, selectedMotorColor, selectedOpening,
    selectedDim, selectedSecondDim, customDimInput, customSecondaryDimInput,
    dimensionQuote, clientHeightM, cfgTouched,
    availableTiers, showTierChoice, availableIfaceTypes, availableProtocols, availableControlTags,
    availableColors, availableProfileColors, availableMotorColors, protocolGroups, visibleProtocolGroups,
    activeVariant, matchAmbiguous, matrix, matrixLayout, dimKey, secondDimKey, uniqueDims,
    dimLabels, exceedsCatalogRange, roundedSelectedDim,
    dimensionValueForV2Api, secondDimensionValueForV2Api, showRoomHeightNote,
    v2PriceLoading, v2BasePrice, v2Surcharges,
    animatedCatalogPrice, animatedCheckoutPrice, pricePulse, displayPrice,
    effectiveDiscountPct, hasPartnerDiscount, retailPrice, savingsPrice,
    showCheckoutDock,
    schemaSteps,
    showWiredPowerChip,
    dualPowerChoice,
    wiredOnlyPower,
    splitColorSteps,
    cfgReveal,
    isChipDisabled, controlDisableReason, toggleControlTag, selectProtocolInGroup,
    setSelectedTierSlug, setSelectedBattery, setSelectedIfaceType, setSelectedProtocol,
    setSelectedColor, setSelectedProfileColor, setSelectedMotorColor,
    setSelectedOpening, setSelectedDim, setSelectedSecondDim, setCustomDimInput, setCustomSecondaryDimInput,
    setClientHeightM, setCfgTouched,
  } = useConfigurator(group, effectiveGroupId, effectiveCatalogSlug, discountPct, {
    isSliding: group ? SLIDING_CATEGORIES.includes(group.category) : false,
    branchSlug: group?.branch_slug ?? null,
    subtypeSlug: group?.subtype_slug ?? null,
    catalogTierSlug: catalogTier,
  });

  useEffect(() => {
    if (loading || !group || !isRazdvizhnyeBranch(group) || catalogTier) return;
    const gid = String(group.group_id || dataGroupId || '').trim();
    if (!gid) return;
    navigate(toGroupUrl(gid, RAZDVIZHNYE_DEFAULT_TIER), { replace: true });
  }, [loading, group, catalogTier, dataGroupId, navigate, from]);
  /** Единый «премиум»-отклик суммы (hero + нижняя панель): масштаб, свечение, плавный easing */
  const premiumPriceRevealStyle = useMemo(
    () =>
      ({
        transition:
          'transform 380ms cubic-bezier(0.16, 1, 0.3, 1), opacity 280ms cubic-bezier(0.16, 1, 0.3, 1), text-shadow 420ms cubic-bezier(0.16, 1, 0.3, 1)',
        transform: pricePulse ? 'scale(1.048) translateZ(0)' : 'scale(1) translateZ(0)',
        opacity: pricePulse ? 0.92 : 1,
        textShadow: pricePulse
          ? '0 0 36px rgba(99,179,237,0.58), 0 0 2px rgba(144,205,244,0.45)'
          : '0 0 16px rgba(99,179,237,0.25)',
        willChange: pricePulse ? 'transform' : 'auto',
      }) as const,
    [pricePulse]
  );

  const useControlStep = !schemaSteps || schemaSteps.includes('control');
  const showLegacyIface =
    availableIfaceTypes.length > 0 && (!schemaSteps || schemaSteps.includes('iface')) && !useControlStep;
  const showLegacyProtocol =
    availableProtocols.length > 0 && (!schemaSteps || schemaSteps.includes('protocol')) && !useControlStep;

  const isPremiumRazdvizhnye = Boolean(
    group && isRazdvizhnyeBranch(group) && catalogTier === 'premium',
  );
  const isTierSplitRazdvizhnye = Boolean(
    group && isRazdvizhnyeBranch(group) && (catalogTier === 'premium' || catalogTier === 'standard'),
  );

  /** Медиа только своей системы tier (catalog_v2_slug / adminSystemId), без вырезания по URL. */
  const tierFilteredGallery = useMemo((): ProductGroupMediaSlide[] => {
    if (!group) return [];
    const fromGroup = Array.isArray(group.gallery) ? group.gallery : [];
    if (!isAdmin || adminGalleryList === null) return fromGroup;
    if (adminGalleryList.length > 0) return adminGalleryList;
    return fromGroup;
  }, [group, isAdmin, adminGalleryList]);

  const slides = useMemo((): HeroSlide[] => {
    if (!group) return [];
    if (tierFilteredGallery.length > 0) {
      return tierFilteredGallery.map((m, i) => {
        const src = resolveBackendMediaUrl(m.url);
        return m.type === 'video'
          ? {
              kind: 'video' as const,
              src,
              posterUrl: m.poster_url,
              key: `m${m.id}-${i}`,
              mediaId: m.id,
            }
          : { kind: 'image' as const, src, key: `m${m.id}-${i}`, mediaId: m.id };
      });
    }
    if (isPremiumRazdvizhnye) return [];
    if (!group.image_url) return [];
    const base = resolveBackendMediaUrl(group.image_url);
    if (!base) return [];
    if (/\/uploads\//i.test(base)) return [{ kind: 'image' as const, src: base, key: 'upload-single' }];
    const dot = base.lastIndexOf('.');
    const stem = dot > 0 ? base.slice(0, dot) : base;
    const ext = dot > 0 ? base.slice(dot) : '.jpg';
    return [
      { kind: 'image' as const, src: base, key: 'stem-0' },
      ...Array.from({ length: 6 }, (_, i) => ({ kind: 'image' as const, src: `${stem}-${i + 1}${ext}`, key: `stem-${i + 1}` })),
    ];
  }, [group, group?.image_url, tierFilteredGallery, isPremiumRazdvizhnye]);

  const effectiveProductGallery = tierFilteredGallery;

  const adminMediaStripGallery = useMemo((): ProductGroupMediaSlide[] => {
    if (!isAdmin) return effectiveProductGallery;
    if (isTierSplitRazdvizhnye) return effectiveProductGallery;
    if (effectiveProductGallery.length > 0) return effectiveProductGallery;
    const hasDbSlide = slides.some((s) => s.mediaId != null && Number(s.mediaId) > 0);
    if (hasDbSlide) return effectiveProductGallery;
    const imgs = slides.filter((s) => s.kind === 'image');
    if (!imgs.length) return [];
    return imgs.map((s, i) => ({ id: -(i + 1), url: s.src, type: 'image' as const, sort_order: i, is_primary: i === 0 }));
  }, [isAdmin, effectiveProductGallery, slides, isTierSplitRazdvizhnye]);

  const v2Gallery = isAdmin ? adminMediaStripGallery : effectiveProductGallery;
  const hasRealCatalogMediaRows = useMemo(() => effectiveProductGallery.some((m) => m.id > 0), [effectiveProductGallery]);
  const adminServerMediaCount = useMemo(() => {
    if (!isAdmin) return 0;
    if (adminGalleryList !== null) return adminGalleryList.length;
    return effectiveProductGallery.filter((m) => m.id > 0).length;
  }, [isAdmin, adminGalleryList, effectiveProductGallery]);
  const adminVisibleMediaCount = v2Gallery.length;
  const adminHasLegacyPreviewTiles = useMemo(() => isAdmin && v2Gallery.some((m) => m.id < 0), [isAdmin, v2Gallery]);
  const adminSlotsUsedForLimit = useMemo(() => {
    if (!isAdmin) return 0;
    return Math.min(CATALOG_MEDIA_LIMIT, Math.max(adminServerMediaCount, adminVisibleMediaCount));
  }, [isAdmin, adminServerMediaCount, adminVisibleMediaCount]);
  const adminSlotsFreeForLimit = Math.max(0, CATALOG_MEDIA_LIMIT - adminSlotsUsedForLimit);
  const adminGalleryAtCapacity = isAdmin && adminSlotsUsedForLimit >= CATALOG_MEDIA_LIMIT;
  const slidesIdentity = useMemo(() => slides.map((s) => s.key).join('|'), [slides]);

  useEffect(() => {
    if (!lightbox) {
      lightboxSlideIdxRef.current = slideIdx;
      setLightboxVideoAutoplay(false);
      return;
    }
    if (lightboxSlideIdxRef.current === slideIdx) return;
    lightboxSlideIdxRef.current = slideIdx;
    setLightboxVideoAutoplay(slides[slideIdx]?.kind === 'video');
  }, [lightbox, slideIdx, slides]);

  useLayoutEffect(() => { setSlideIdx(0); setFailedSlides(new Set()); }, [slidesIdentity]);

  const reloadProductGroup = useCallback(
    async (opts?: { preserveShort?: string; preserveFull?: string }) => {
      if (!effectiveGroupId) return;
      try {
        const g = await getGroupById(effectiveGroupId, { tier: catalogTier });
        const galleryNorm = parseV2SystemMedia((g as any)?.gallery);
        const merged = mergeProductGroupCopyFromApi(
          g,
          catalogTier === 'premium' ? null : group,
        );
        const short = opts?.preserveShort?.trim() || merged.short || undefined;
        const full = opts?.preserveFull?.trim() || merged.full || undefined;
        setGroup({
          ...g,
          gallery: galleryNorm.length ? galleryNorm : [],
          protocols: Array.isArray((g as any)?.protocols) ? (g as any).protocols : [],
          interface_types: Array.isArray((g as any)?.interface_types) ? (g as any).interface_types : [],
          variants: Array.isArray((g as any)?.variants) ? (g as any).variants : [],
          advantages: Array.isArray((g as any)?.advantages) ? (g as any).advantages : [],
          control_types: Array.isArray((g as any)?.control_types) ? (g as any).control_types : [],
          subtype_tags: Array.isArray((g as any)?.subtype_tags) ? (g as any).subtype_tags : [],
          selector_schema: (g as any)?.selector_schema || {},
          ...(short
            ? {
                short_summary: short,
                short_description: short,
                marketing_description: short,
              }
            : {}),
          ...(full !== undefined ? { description: full } : {}),
        });
        if (short) setAdminDescDraft(short);
        await reloadAdminGallery();
      } catch {
        /* keep current */
      }
    },
    [effectiveGroupId, group, catalogTier, reloadAdminGallery],
  );

  const triggerReplaceMediaPick = useCallback((row: ProductGroupMediaSlide) => {
    const inp = adminReplaceMediaInputRef.current;
    if (!inp) return;
    inp.accept = row.type === 'video' ? 'video/mp4,.mp4' : 'image/png,image/jpeg,image/webp';
    inp.dataset.replaceForId = String(row.id);
    inp.click();
  }, []);

  // Auto-focus on next step after selection
  useEffect(() => {
    if (skipAutoFocusRef.current) return;
    if (selectedOpening !== null && useControlStep) {
      setTimeout(() => controlBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
    }
  }, [selectedOpening, useControlStep]);
  useEffect(() => {
    if (skipAutoFocusRef.current) return;
    if (selectedIfaceType !== null) setTimeout(() => protocolBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
  }, [selectedIfaceType]);
  useEffect(() => {
    if (skipAutoFocusRef.current) return;
    if (selectedProtocol !== null) {
      const target =
        availableProfileColors.length > 0 ? profileColorBlockRef
          : availableColors.length > 0 ? colorBlockRef
            : sizeBlockRef;
      setTimeout(() => target.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
    }
  }, [selectedProtocol, availableColors.length, availableProfileColors.length]);
  useEffect(() => {
    if (skipAutoFocusRef.current) return;
    if (selectedMotorColor !== null && uniqueDims.length > 0) {
      setTimeout(() => sizeBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
    }
  }, [selectedMotorColor, uniqueDims.length]);
  useEffect(() => {
    if (loading) return;
    const timer = setTimeout(() => { skipAutoFocusRef.current = false; }, 350);
    return () => clearTimeout(timer);
  }, [loading, effectiveGroupId]);

  // Category hero meta
  const meta = group ? getCategoryMeta(group.category) : getCategoryMeta('');

  // Min price for display before configurator interaction
  const minPrice = group?.min_price ?? (matrix.length ? Math.min(...matrix.map(p => p.price)) : 0);

  const [globalStepCopy, setGlobalStepCopy] = useState<StepCopyOverrides | null>(null);
  useEffect(() => {
    getConfiguratorStepCopy()
      .then((data) => setGlobalStepCopy(parseStepCopyOverrides({ step_copy: data.step_copy })))
      .catch(() => setGlobalStepCopy(null));
  }, []);

  const stepCopySchema = useMemo(
    () => buildMergedStepCopySchema(globalStepCopy, group?.selector_schema as Record<string, unknown> | undefined),
    [globalStepCopy, group?.selector_schema],
  );

  // Subtype sections for variant chips
  // Disabled for V3 systems — they use the sibling tabs strip instead
  const isV3DirectSystem = effectiveGroupId
    ? !effectiveGroupId.startsWith('v2::') && !effectiveGroupId.startsWith('st::') && !String(effectiveGroupId).match(/^\d+$/)
    : false;
  const pimBranchKey = useMemo(
    () =>
      pimBranchKeyFromSlug(
        group?.branch_slug ||
          (group?.group_id?.includes('::') ? group.group_id.split('::')[0] : '') ||
          group?.category,
      ),
    [group?.branch_slug, group?.group_id, group?.category],
  );
  const pimUi = pimBranchKey ? PIM_BRANCH_UI[pimBranchKey] : null;
  const isPimBranch = Boolean(pimUi);

  const subtypeSections = useMemo(() => {
    if (!group || siblingGroups.length < 2) return [];
    if (isV3DirectSystem || isPimBranch) return [];
    const map = new Map<string, Array<{ key: string; label: string; group_id: string; active: boolean }>>();
    const COLOR_WORDS = ['белый', 'черный', 'чёрный', 'серый', 'серебристый', 'premium', 'премиум', 'белое золото', 'золотой', 'матовый', 'глянцевый'];
    for (const sg of siblingGroups) {
      const tags = Array.isArray(sg.subtype_tags) ? sg.subtype_tags : [];
      for (const tag of tags) {
        if (!tag?.includes('::')) continue;
        const [section, label] = tag.split('::', 2);
        if (!section || !label) continue;
        if (COLOR_WORDS.some(c => label.toLowerCase() === c || label.toLowerCase().startsWith(c))) continue;
        const key = `${section}::${label}`;
        const bucket = map.get(section) || [];
        const existingIdx = bucket.findIndex((x) => x.key === key);
        if (existingIdx === -1) {
          bucket.push({ key, label, group_id: sg.group_id, active: sg.group_id === effectiveGroupId });
        } else {
          const prev = bucket[existingIdx];
          const prevScore = prev.group_id === effectiveGroupId ? 1000 : 0;
          const newScore = sg.group_id === effectiveGroupId ? 1000 : (Number(sg.variants_count ?? 0) * 2);
          if (newScore > prevScore) bucket[existingIdx] = { ...prev, group_id: sg.group_id, active: sg.group_id === effectiveGroupId };
        }
        map.set(section, bucket);
      }
    }
    if (map.size > 0) return [...map.entries()].map(([section, items]) => ({ section, items })).filter((x) => x.items.length > 0);
    return [];
  }, [group, siblingGroups, effectiveGroupId, isV3DirectSystem, isPimBranch]);

  const isSliding = group ? SLIDING_CATEGORIES.includes(group.category) : false;
  const isRulonnye = useMemo(() => {
    const cat = (group?.category || '').toLowerCase();
    const branch = (group?.branch_slug || '').toLowerCase();
    return branch === 'rulonnye' || cat.includes('рулон');
  }, [group?.category, group?.branch_slug]);

  useEffect(() => {
    if (!isRulonnye || !effectiveGroupId || siblingGroups.length === 0) return;
    setRulonNav(selectionFromGroupId(effectiveGroupId, siblingGroups));
  }, [isRulonnye, effectiveGroupId, siblingGroups]);

  const [rulonNavCopy, setRulonNavCopy] = useState<RulonNavigatorUiCopy | null>(null);
  useEffect(() => {
    if (!isRulonnye) {
      setRulonNavCopy(null);
      return;
    }
    getRulonNavigatorUi()
      .then(setRulonNavCopy)
      .catch(() => setRulonNavCopy(null));
  }, [isRulonnye]);

  const showPimSubtypeNav = Boolean(
    pimUi && !pimUi.singleSubtype && siblingGroups.length > 0,
  );

  useEffect(() => {
    if (loading || !group || !pimUi) return;
    const variants = Array.isArray(group.variants) ? group.variants.length : 0;
    const gid = String(group.group_id || '');
    const sub = subtypeSlugFromGroupId(gid);
    const onPimCard = gid.includes('::') && pimUi.subtypeSlugs.has(sub);
    if (onPimCard && variants > 0) return;

    const branch =
      (group.branch_slug || gid.split('::')[0] || pimUi.branchSlugs[0] || '').trim().toLowerCase();
    const target = pimUi.singleSubtype
      ? findSiblingForSubtype(siblingGroups, branch, pimUi.singleSubtype)
      : bestPimSibling(siblingGroups, pimUi);
    if (target?.group_id && target.group_id !== effectiveGroupId) {
      navigate(toGroupUrl(target.group_id), { replace: true });
      return;
    }
    if (!branch) return;
    getGroupedProducts({ category: branch })
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        const compact = list.map((g: { group_id?: string; variants_count?: number }) => ({
          group_id: String(g?.group_id || ''),
          name: '',
          variants_count: Number(g?.variants_count ?? 0),
        }));
        const hit = pimUi.singleSubtype
          ? findSiblingForSubtype(compact, branch, pimUi.singleSubtype)
          : bestPimSibling(compact, pimUi);
        if (hit?.group_id && hit.group_id !== effectiveGroupId) {
          navigate(toGroupUrl(hit.group_id), { replace: true });
          return;
        }
        const pimKey = pimBranchKeyFromSlug(branch);
        const fallback = pimKey ? defaultPimGroupId(pimKey) : null;
        if (fallback && fallback !== effectiveGroupId) {
          navigate(toGroupUrl(fallback), { replace: true });
        }
      })
      .catch(() => undefined);
  }, [loading, group, pimUi, siblingGroups, effectiveGroupId, navigate]);
  const isZipSystem = group?.category === 'ZIP-система';
  const isMarkizaSystem = group?.category === 'Маркиза';
  const categoryHeroNote = isZipSystem
    ? 'ZIP-система для фасадов и террас: стабильность полотна в направляющих и защита от ветра.'
    : isMarkizaSystem
      ? 'Маркизная система для террас и веранд: теневая архитектура и премиальная уличная эстетика.'
      : 'Конфигуратор ниже помогает быстро собрать решение под ваш объект.';
  const specs = group?.specs ?? {};
  const LEGACY_SPEC_KEYS = new Set(['max_width_mm','max_height_mm','min_width_mm','profile_width_mm','max_load_kg','materials','compatible_motors','power_options','other']);
  const cleanSpecs = Object.entries(specs).filter(([k, v]) => !LEGACY_SPEC_KEYS.has(k) && v !== null && v !== undefined && v !== '' && !Array.isArray(v) && typeof v !== 'object');
  // V3 derived specs from variants
  const allVariants = group?.variants ?? [];
  const uniqueMotors = [...new Set(allVariants.map((v: any) => v.motor_model).filter(Boolean))];
  const uniqueProtocols = [...new Set(allVariants.map((v: any) => v.protocol).filter(Boolean))];
  const uniqueColors = [...new Set(allVariants.map((v: any) => v.color).filter(Boolean))];
  const v3DerivedSpecs: [string, string][] = [
    ...(uniqueMotors.length ? [['Моторы', uniqueMotors.join(', ')] as [string,string]] : []),
    ...(uniqueProtocols.length ? [['Протоколы', uniqueProtocols.join(', ')] as [string,string]] : []),
    ...(uniqueColors.length ? [['Цвета', uniqueColors.join(', ')] as [string,string]] : []),
  ];
  const mergedSpecs: [string, unknown][] = [
    ...cleanSpecs,
    ...v3DerivedSpecs.filter(([k]) => !cleanSpecs.find(([ck]) => ck === k)),
  ];
  const hasSpecs = mergedSpecs.length > 0 || activeVariant?.power_watts;

  const needsSize =
    matrixLayout.mode !== 'none' &&
    !sizeSelectionComplete(matrixLayout, selectedDim, selectedSecondDim);
  const canAddToCart =
    !needsSize &&
    (!!activeVariant ||
      !!effectiveCatalogSlug ||
      !!group?.catalog_v2_slug ||
      !!group?.subtype_id);
  type StepKey = 'tier' | 'power' | 'opening' | 'iface' | 'protocol' | 'control' | 'color' | 'profile_color' | 'motor_color' | 'size';

  const stepState = useMemo(() => {
    if (!group) {
      return {
        nextKey: 'done' as const,
        doneCount: 0,
        totalSteps: 1,
      };
    }
    const tierDone = !showTierChoice || availableTiers.length === 0 || (cfgTouched.tier && selectedTierSlug !== null);
    const powerDone =
      !group.has_wired
      || (wiredOnlyPower && cfgTouched.power && selectedBattery === false)
      || (dualPowerChoice && cfgTouched.power && selectedBattery !== null);
    const openingDone = !isSliding || (cfgTouched.opening && selectedOpening !== null);
    const ifaceDone = !showLegacyIface || cfgTouched.iface;
    const protocolDone = !showLegacyProtocol || cfgTouched.protocol;
    const controlDone =
      !useControlStep
      || (
        (availableControlTags.length === 0 || selectedControlTags.length > 0 || cfgTouched.control)
        && (visibleProtocolGroups.length === 0 || availableProtocols.length === 0 || selectedProtocol !== null || cfgTouched.control)
      );
    const usesSplitColorSteps = splitColorSteps;
    const colorDone = usesSplitColorSteps || availableColors.length === 0 || cfgTouched.color;
    const profileColorDone = availableProfileColors.length === 0 || (cfgTouched.profile_color && selectedProfileColor !== null);
    const motorColorDone = availableMotorColors.length === 0 || (cfgTouched.motor_color && selectedMotorColor !== null);
    const sizeDone = sizeSelectionComplete(matrixLayout, selectedDim, selectedSecondDim);

    const plan: StepKey[] = [];
    const stepAllowed = (k: string) => !schemaSteps || schemaSteps.includes(k);
    if (showTierChoice && stepAllowed('tier')) plan.push('tier');
    if ((dualPowerChoice || wiredOnlyPower) && stepAllowed('power')) plan.push('power');
    if (isSliding && stepAllowed('opening')) plan.push('opening');
    if (useControlStep && (availableControlTags.length > 0 || visibleProtocolGroups.length > 0 || availableProtocols.length > 0)) {
      plan.push('control');
    }
    if (showLegacyIface) plan.push('iface');
    if (showLegacyProtocol) plan.push('protocol');
    if (availableColors.length > 0 && stepAllowed('color')) plan.push('color');
    if (availableProfileColors.length > 0 && stepAllowed('profile_color')) plan.push('profile_color');
    if (availableMotorColors.length > 0 && stepAllowed('motor_color')) plan.push('motor_color');
    if (matrixLayout.mode !== 'none' && stepAllowed('size')) plan.push('size');

    const doneFlags: Record<StepKey, boolean> = {
      tier: tierDone,
      power: powerDone,
      opening: openingDone,
      iface: ifaceDone,
      protocol: protocolDone,
      control: controlDone,
      color: colorDone,
      profile_color: profileColorDone,
      motor_color: motorColorDone,
      size: sizeDone,
    };
    const doneCount = plan.filter((k) => doneFlags[k]).length;
    const totalSteps = Math.max(1, plan.length);

    let nextKey: StepKey | 'done' = 'done';
    for (const k of plan) {
      if (!doneFlags[k]) {
        nextKey = k;
        break;
      }
    }

    return { nextKey, doneCount, totalSteps };
  }, [
    group,
    isSliding,
    cfgTouched,
    selectedOpening,
    selectedTierSlug,
    selectedControlTags,
    selectedProtocol,
    selectedProfileColor,
    selectedMotorColor,
    showTierChoice,
    availableTiers.length,
    availableIfaceTypes.length,
    availableProtocols.length,
    availableControlTags.length,
    availableColors.length,
    availableProfileColors.length,
    availableMotorColors.length,
    matrixLayout,
    selectedDim,
    selectedSecondDim,
    schemaSteps,
    useControlStep,
    showLegacyIface,
    showLegacyProtocol,
    protocolGroups.length,
    dualPowerChoice,
    wiredOnlyPower,
    selectedBattery,
  ]);
  const hasV2Breakdown = v2BasePrice !== null || v2Surcharges.length > 0;
  const handleAddToCart = async () => {
    if (!token) { navigate('/profile'); return; }
    const systemSlugForCart =
      effectiveCatalogSlug ||
      (group?.catalog_v2_slug ? String(group.catalog_v2_slug).trim() : '') ||
      null;
    const isPimCatalogGroup =
      Boolean(systemSlugForCart) ||
      Boolean(group?.subtype_id) ||
      Boolean(effectiveGroupId?.includes('::') && !effectiveGroupId?.startsWith('v2::'));
    if (!isPimCatalogGroup && !activeVariant) return;
    if (needsSize) {
      setValidationHint('Выберите размер перед добавлением в корзину');
      sizeBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      haptic.warning();
      return;
    }
    setValidationHint(null);
    setAdding(true);
    try {
      const hParsed = parseClientHeightM(clientHeightM);
      const clientHeightOk = hParsed != null;
      if (isPimCatalogGroup && (systemSlugForCart || activeVariant)) {
        const cfg = buildCatalogV2SelectionBody({
          selectedIfaceType,
          selectedProtocol,
          selectedControlTags,
          selectedTierSlug,
          selectedColor,
          selectedProfileColor,
          selectedMotorColor,
          selectedOpening,
          dimKey,
          dimensionValue: dimensionValueForV2Api,
          secondDimKey,
          secondDimensionValue: secondDimensionValueForV2Api,
        });
        const pimVariantId =
          activeVariant && (activeVariant.catalog_variant_id ?? activeVariant.id) > 0
            ? activeVariant.catalog_variant_id ?? activeVariant.id
            : undefined;
        await addSystemV2ToCart({
          system_slug: systemSlugForCart || '',
          catalog_variant_id: pimVariantId,
          quantity: 1,
          ...cfg,
          client_height_m: clientHeightOk && hParsed != null ? hParsed : undefined,
          price_on_request: exceedsCatalogRange ? true : undefined,
        });
      } else if (activeVariant && activeVariant.id > 0) {
        const payload: {
          product_id: number;
          quantity: number;
          length_m?: number;
          width_m?: number;
          projection_m?: number;
          custom_params?: Record<string, unknown>;
        } = { product_id: activeVariant.id, quantity: 1 };
        if (dimKey && dimensionValueForV2Api != null) {
          if (dimKey === 'length_m') payload.length_m = dimensionValueForV2Api;
          else if (dimKey === 'width_m') payload.width_m = dimensionValueForV2Api;
          else if (dimKey === 'projection_m') payload.projection_m = dimensionValueForV2Api;
        }
        if (secondDimKey && secondDimensionValueForV2Api != null) {
          if (secondDimKey === 'length_m') payload.length_m = secondDimensionValueForV2Api;
          else if (secondDimKey === 'width_m') payload.width_m = secondDimensionValueForV2Api;
          else if (secondDimKey === 'projection_m') payload.projection_m = secondDimensionValueForV2Api;
        }
        const cp: Record<string, unknown> = {};
        if (selectedOpening) cp.opening_type = selectedOpening;
        if (clientHeightOk && hParsed != null) cp.client_height_m = hParsed;
        if (Object.keys(cp).length) payload.custom_params = cp;
        await addToCart(payload);
      } else {
        setValidationHint('Не удалось определить систему для корзины. Обновите страницу.');
        haptic.error();
        return;
      }
      try {
        const cart = await getCart();
        setCart(cart.items);
      } catch {
        /* badge best-effort */
      }
      setAdded(true);
      haptic.success();
      setTimeout(() => setAdded(false), 2500);
    } catch (e: unknown) {
      const detail =
        e && typeof e === 'object' && 'response' in e
          ? String((e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail ?? '')
          : '';
      setValidationHint(
        detail
          ? (typeof detail === 'string' ? detail : 'Ошибка добавления в корзину')
          : 'Не удалось добавить в корзину. Проверьте интернет и войдите через бота.',
      );
      haptic.error();
    } finally { setAdding(false); }
  };

  const focusBlock = (ref: { current: HTMLDivElement | null }) => {
    if (!ref.current) return;
    ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  useEffect(() => {
    if (skipAutoFocusRef.current) return;
    if (selectedIfaceType !== null) {
      setTimeout(() => focusBlock(protocolBlockRef), 80);
    }
  }, [selectedIfaceType]);

  useEffect(() => {
    if (skipAutoFocusRef.current) return;
    if (selectedProtocol !== null) {
      const target = availableColors.length > 0 ? colorBlockRef : sizeBlockRef;
      setTimeout(() => focusBlock(target), 80);
    }
  }, [selectedProtocol, availableColors.length]);

  useEffect(() => {
    if (skipAutoFocusRef.current) return;
    if (selectedColor !== null && uniqueDims.length > 0) {
      setTimeout(() => focusBlock(sizeBlockRef), 80);
    }
  }, [selectedColor, uniqueDims.length]);

  useEffect(() => {
    if (loading) return;
    const timer = setTimeout(() => {
      skipAutoFocusRef.current = false;
    }, 350);
    return () => clearTimeout(timer);
  }, [loading, effectiveGroupId]);

  const openCatalogMediaCrop = useCallback(
    async (file: File, replaceMediaId: number | null) => {
      const normalized = await prepareCatalogMediaForUpload(file);
      await openAdminCrop({
        file: normalized,
        defaultPreset: 'catalog_wide',
        allowedPresets: CATALOG_PDP_CROP_PRESETS,
        title: replaceMediaId != null ? 'Кадрирование и замена' : 'Новое фото в карусель',
        onConfirm: async (cropped: File) => {
          if (!adminSystemId) return;
          setAdminUploadingImage(true);
          try {
            if (replaceMediaId != null) {
              await replaceAdminCatalogSystemMedia(adminSystemId, replaceMediaId, cropped);
            } else {
              await appendAdminCatalogSystemMedia(adminSystemId, cropped);
            }
            await reloadProductGroup();
            haptic.success();
          } catch {
            haptic.error();
          } finally {
            setAdminUploadingImage(false);
          }
        },
        onConfirmRaw: async (raw: File) => {
          if (!adminSystemId) return;
          setAdminUploadingImage(true);
          try {
            const uploadFile = await prepareCatalogMediaForUpload(raw);
            if (replaceMediaId != null) {
              await replaceAdminCatalogSystemMedia(adminSystemId, replaceMediaId, uploadFile);
            } else {
              await appendAdminCatalogSystemMedia(adminSystemId, uploadFile);
            }
            await reloadProductGroup();
            haptic.success();
          } catch {
            haptic.error();
            window.alert(CATALOG_UPLOAD_FAIL_MSG);
          } finally {
            setAdminUploadingImage(false);
          }
        },
      });
    },
    [adminSystemId, openAdminCrop, reloadProductGroup, haptic],
  );

  // ─── Loading / Error ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col min-h-screen overflow-hidden">
        <SkeletonProductGroup />
      </div>
    );
  }
  if (!group) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <span className="text-5xl opacity-40">◽</span>
        <p className="font-semibold" style={{ color: 'var(--text-2)' }}>Решение не найдено</p>
        <p className="text-xs text-center max-w-xs" style={{ color: 'var(--text-3)' }}>
          Возможно, позиция обновлена в каталоге. Вернитесь к витрине и выберите актуальную конфигурацию.
        </p>
        <button onClick={goBack} style={{ color: 'var(--cyan)' }} className="text-sm font-medium">Вернуться в каталог</button>
      </div>
    );
  }

  const moveGalleryItem = async (mediaId: number, dir: -1 | 1) => {
    if (!adminSystemId || mediaId <= 0) return;
    const ids = v2Gallery.filter((m) => m.id > 0).map((m) => m.id);
    const idx = ids.indexOf(mediaId);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= ids.length) return;
    haptic.selection();
    const next = [...ids];
    [next[idx], next[j]] = [next[j], next[idx]];
    try {
      await reorderAdminCatalogSystemMedia(adminSystemId, next);
      await reloadProductGroup();
      haptic.success();
    } catch {
      haptic.error();
    }
  };

  // ─── JSX ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-screen overflow-x-hidden">
      {adminCropModal}

      {/* ── LIGHTBOX ── */}
      {lightbox && slides.length > 0 && (
        <div
          className="fixed inset-0 z-[85] bg-black flex flex-col select-none"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          onClick={() => {
            if (!lightboxZoomed) setLightbox(false);
          }}
          onTouchStart={(e) => {
            if (e.touches.length !== 1 || lightboxZoomed) return;
            setTouchStartX(e.touches[0].clientX);
          }}
          onTouchEnd={(e) => {
            if (lightboxZoomed || touchStartX === null) return;
            const dx = e.changedTouches[0].clientX - touchStartX;
            if (Math.abs(dx) > 50 && slides.length > 0) {
              setSlideIdx((i) => {
                const next = dx < 0 ? (i + 1) % slides.length : (i - 1 + slides.length) % slides.length;
                return next;
              });
            }
            setTouchStartX(null);
          }}
        >
          <div className="flex items-center px-4 pt-10 pb-2 gap-3 min-h-[52px]">
            <span
              className="text-[11px] font-semibold px-3 py-1 rounded-full shrink-0"
              style={{ background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}
            >
              {slideIdx + 1} / {slides.length}
            </span>
            <div className="flex-1 min-w-2" />
            {isAdmin && adminSystemId && slides[slideIdx]?.kind === 'video' && slides[slideIdx]?.mediaId != null && (
                <button
                  aria-label="Скачать видео"
                  disabled={adminDownloadingMediaId === slides[slideIdx]?.mediaId}
                  onClick={async (e) => {
                    e.stopPropagation();
                    const cur = slides[slideIdx];
                    const mid = cur?.mediaId;
                    if (!cur || mid == null) return;
                    haptic.selection();
                    setAdminDownloadingMediaId(mid);
                    try {
                      await downloadBackendMediaFile(cur.src, { mediaId: mid, kind: 'video' });
                      haptic.success();
                    } catch {
                      haptic.error();
                    } finally {
                      setAdminDownloadingMediaId(null);
                    }
                  }}
                  className="text-[11px] font-semibold px-2.5 py-1.5 rounded-full shrink-0 touch-manipulation inline-flex items-center gap-1"
                  style={{ background: 'rgba(99,179,237,0.18)', color: 'var(--cyan-bright)', border: '1px solid rgba(99,179,237,0.4)' }}
                >
                  <Download size={12} strokeWidth={2.25} />
                  {adminDownloadingMediaId === slides[slideIdx]?.mediaId ? '…' : 'Скачать'}
                </button>
              )}
            {isAdmin && adminSystemId && slides[slideIdx]?.mediaId != null && (
                <button
                  aria-label="Удалить этот слайд"
                  disabled={adminDeletingImage}
                  onClick={async (e) => {
                    e.stopPropagation();
                    const mid = slides[slideIdx]?.mediaId;
                    if (mid == null) return;
                    haptic.selection();
                    setAdminDeletingImage(true);
                    try {
                      await deleteAdminCatalogSystemMediaItem(adminSystemId, mid);
                      await reloadProductGroup();
                      setFailedSlides(new Set());
                      setSlideIdx(0);
                      setLightbox(false);
                      haptic.success();
                    } catch {
                      haptic.error();
                    } finally {
                      setAdminDeletingImage(false);
                    }
                  }}
                  className="text-[11px] font-semibold px-2.5 py-1.5 rounded-full shrink-0 touch-manipulation"
                  style={{ background: 'rgba(252,129,74,0.2)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.45)' }}
                >
                  {adminDeletingImage ? 'Удаляю...' : 'Удалить слайд'}
                </button>
              )}
            {isAdmin && adminSystemId && slides.length > 0 && !slides.some((s) => s.mediaId != null) && group?.image_url && (
                <button
                  aria-label="Удалить фото карточки"
                  disabled={adminDeletingImage}
                  onClick={async (e) => {
                    e.stopPropagation();
                    haptic.selection();
                    setAdminDeletingImage(true);
                    try {
                      await deleteAdminCatalogSystemImage(adminSystemId);
                      await updateAdminGroupContent({ group_id: group.group_id, image_url: null });
                      setGroup((prev) => (prev ? { ...prev, image_url: undefined, gallery: undefined } : prev));
                      setFailedSlides(new Set());
                      setSlideIdx(0);
                      setLightbox(false);
                      haptic.success();
                    } catch {
                      haptic.error();
                    } finally {
                      setAdminDeletingImage(false);
                    }
                  }}
                  className="text-[11px] font-semibold px-2.5 py-1.5 rounded-full shrink-0 touch-manipulation"
                  style={{ background: 'rgba(252,129,74,0.2)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.45)' }}
                >
                  {adminDeletingImage ? 'Удаляю...' : 'Удалить фото'}
                </button>
              )}
            <button
              aria-label="Закрыть полноэкранный просмотр"
              onClick={(e) => {
                e.stopPropagation();
                haptic.selection();
                setLightboxZoomed(false);
                setLightbox(false);
              }}
              className="shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full text-white/90 text-lg touch-manipulation ml-1"
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}
            >
              ✕
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center overflow-hidden relative">
            {slides.length > 1 && (
              <button
                aria-label="Предыдущий слайд"
                onClick={e => { e.stopPropagation(); setSlideIdx(i => (i - 1 + slides.length) % slides.length); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center z-10 text-white text-xl"
                style={{ background: 'rgba(99,179,237,0.18)', backdropFilter: 'blur(8px)', border: '1px solid rgba(99,179,237,0.3)' }}
              >‹</button>
            )}
            {slides[slideIdx]?.kind === 'video' ? (
              <div className="relative w-full h-full max-h-[85vh] flex items-center justify-center">
                <ProductGroupHeroVideo
                  key={slides[slideIdx]!.key}
                  src={slides[slideIdx]!.src}
                  posterUrl={slides[slideIdx]!.posterUrl}
                  show
                  theater
                  onCollapseTheater={collapseVideoLightbox}
                  autoPlay={lightboxVideoAutoplay}
                  onError={() => {}}
                  stopClickBubble
                />
              </div>
            ) : slides[slideIdx] ? (
              <ProductLightboxPinchImage
                key={slides[slideIdx].key}
                slideKey={slides[slideIdx].key}
                src={slides[slideIdx].src}
                alt={group.name}
                onZoomChange={setLightboxZoomed}
              />
            ) : null}
            {slides.length > 1 && (
              <button
                aria-label="Следующий слайд"
                onClick={e => { e.stopPropagation(); setSlideIdx(i => (i + 1) % slides.length); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center z-10 text-white text-xl"
                style={{ background: 'rgba(99,179,237,0.18)', backdropFilter: 'blur(8px)', border: '1px solid rgba(99,179,237,0.3)' }}
              >›</button>
            )}
          </div>
          <div
            className="flex justify-center gap-3 py-4 shrink-0"
            style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
          >
            {slides.map((s, i) => (
              <button
                key={s.key}
                aria-label={`Открыть слайд ${i + 1}`}
                onClick={e => { e.stopPropagation(); setSlideIdx(i); haptic.selection(); }}
                className="transition-all rounded-full"
                style={i === slideIdx
                  ? { background: 'var(--cyan)', width: '20px', height: '6px' }
                  : { background: 'rgba(99,179,237,0.3)', width: '6px', height: '6px' }
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* ── CAROUSEL ── */}
      <div
        className="relative transition-all duration-300"
        style={{
          background: '#0A192F',
          opacity: heroReady ? 1 : 0.72,
          transform: heroReady ? 'scale(1)' : 'scale(0.992)',
        }}
      >
        <div className="overflow-hidden relative" style={{ height: '45vh', minHeight: '260px', maxHeight: '400px' }} onClick={() => {
            const cur = slides[slideIdx];
            if (!cur || failedSlides.has(slideIdx)) return;
            setLightboxZoomed(false);
            setCarouselVideoAutoplay(false);
            setLightboxVideoAutoplay(false);
            lightboxSlideIdxRef.current = slideIdx;
            setLightbox(true);
            haptic.selection();
          }}>
          {slides.length > 0 ? (
            slides.map((slide, i) => (
              <ProductGroupHeroSlideLayer
                key={slide.key}
                kind={slide.kind}
                src={slide.src}
                posterUrl={slide.kind === 'video' ? slide.posterUrl : undefined}
                alt={group.name}
                mediaDomKey={slide.key}
                show={i === slideIdx && !failedSlides.has(i)}
                stopClickBubble={slide.kind === 'video'}
                onVideoExpand={slide.kind === 'video' ? openVideoLightbox : undefined}
                videoAutoPlay={slide.kind === 'video' && i === slideIdx && carouselVideoAutoplay}
                onDecodeError={() => {
                  setFailedSlides((prev) => {
                    const next = new Set(prev);
                    next.add(i);
                    if (i === slideIdx) {
                      const nextValid = slides.findIndex((_, j) => j > i && !next.has(j));
                      if (nextValid !== -1) setSlideIdx(nextValid);
                    }
                    return next;
                  });
                }}
              />
            ))
          ) : null}
          {(slides.length === 0 || failedSlides.size === slides.length) && (
            <div className={`w-full h-full bg-gradient-to-br ${meta.gradient} flex items-center justify-center`}>
              <span className="text-8xl opacity-30">{meta.emoji}</span>
            </div>
          )}
          {slides.length > 0 && failedSlides.size < slides.length && slides[slideIdx]?.kind !== 'video' && (
            <div className="absolute bottom-3 right-3 rounded-full p-1.5 pointer-events-none"
              style={{ background: 'rgba(99,179,237,0.15)', backdropFilter: 'blur(8px)', border: '1px solid rgba(99,179,237,0.25)' }}>
              <span className="text-white text-xs">⤢</span>
            </div>
          )}
        </div>

        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/30 pointer-events-none" />

        {slides.length > 1 && (
          <>
            <button
              aria-label="Предыдущий слайд"
              onClick={e => { e.stopPropagation(); setSlideIdx(i => (i - 1 + slides.length) % slides.length); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center z-10"
              style={{ background: 'rgba(10,25,47,0.55)', backdropFilter: 'blur(8px)', border: '1px solid rgba(99,179,237,0.2)' }}
            >
              <span className="text-white text-sm">‹</span>
            </button>
            <button
              aria-label="Следующий слайд"
              onClick={e => { e.stopPropagation(); setSlideIdx(i => (i + 1) % slides.length); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center z-10"
              style={{ background: 'rgba(10,25,47,0.55)', backdropFilter: 'blur(8px)', border: '1px solid rgba(99,179,237,0.2)' }}
            >
              <span className="text-white text-sm">›</span>
            </button>
          </>
        )}

        {slides.length > 1 && (
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
            {slides.map((s, i) => (
              <button
                key={s.key}
                aria-label={`Показать слайд ${i + 1}`}
                onClick={e => { e.stopPropagation(); setSlideIdx(i); haptic.selection(); }}
                className="rounded-full transition-all duration-200"
                style={i === slideIdx
                  ? { background: 'var(--cyan)', width: '16px', height: '4px' }
                  : { background: 'rgba(99,179,237,0.35)', width: '4px', height: '4px' }
                }
              />
            ))}
          </div>
        )}

        <button
          aria-label="Назад в каталог"
          onClick={goBack}
          className="absolute top-4 left-4 p-2 rounded-full z-10"
          style={{ background: 'rgba(10,25,47,0.6)', backdropFilter: 'blur(8px)', border: '1px solid rgba(99,179,237,0.2)' }}
        >
          <ArrowLeft size={18} color="var(--cyan-bright)" />
        </button>

      </div>

      {/* ── TITLE + PRICE CARD ── */}
      <div className="px-4 pt-4 pb-3" style={{ borderBottom: '1px solid var(--border-glass)' }}>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {group.is_premium && (
            <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: 'rgba(245,197,24,0.15)', color: '#F5C518', border: '1px solid rgba(245,197,24,0.3)' }}>
              <Star size={8} fill="currentColor" /> Premium
            </span>
          )}
          {group.has_battery && (
            <span className="badge-cyan flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full">
              <BatteryCharging size={8} /> АКБ
            </span>
          )}
          {(group.category === 'Маркиза и ZIP-система' || group.category === 'Маркиза' || group.category === 'ZIP-система') && (
            <>
              <span className="badge-cyan text-[10px] font-bold px-2 py-0.5 rounded-full">🌦 Датчик дождя</span>
              <span className="badge-cyan text-[10px] font-bold px-2 py-0.5 rounded-full">💨 Датчик ветра</span>
              {isZipSystem && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(45,212,191,0.14)', color: '#5EEAD4', border: '1px solid rgba(45,212,191,0.35)' }}>
                  🛡️ Ветровая стабильность
                </span>
              )}
              {isMarkizaSystem && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(251,191,36,0.14)', color: '#FCD34D', border: '1px solid rgba(251,191,36,0.35)' }}>
                  ☀️ Архитектурная тень
                </span>
              )}
            </>
          )}
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-3)', border: '1px solid var(--border-subtle)' }}>
            {meta.emoji} {group.category}
          </span>
        </div>
        <h1 className="text-lg sm:text-xl font-black leading-tight" style={{ color: 'var(--text-1)' }}>{group.name}</h1>
        {group.subcategory && group.subcategory !== group.name && (
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{group.subcategory}</p>
        )}
        {groupFullCopy && (
          <p className="text-xs mt-2 leading-relaxed" style={{ color: 'var(--text-2)' }}>
            {groupFullCopy}
          </p>
        )}
        {isAdmin && (
          <div className="mt-2">
            <div className="flex flex-wrap gap-2">
                {adminSystemId ? (
                    <div className="flex flex-col gap-2 w-full min-w-0">
                      <input
                        ref={adminAppendMediaInputRef}
                        type="file"
                        className="hidden"
                        accept="image/*,video/mp4,.mp4"
                        disabled={adminUploadingImage}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          e.currentTarget.value = '';
                          if (!file || !adminSystemId) {
                            haptic.error();
                            return;
                          }
                          if (adminGalleryAtCapacity) {
                            haptic.error();
                            return;
                          }
                          const isVideo = isCatalogVideoFile(file);
                          if (isVideo) {
                            setAdminUploadingImage(true);
                            try {
                              await appendAdminCatalogSystemMedia(adminSystemId, file);
                              await reloadProductGroup();
                              haptic.success();
                            } catch {
                              haptic.error();
                            } finally {
                              setAdminUploadingImage(false);
                            }
                            return;
                          }
                          void openCatalogMediaCrop(file, null);
                        }}
                      />
                      <input
                        ref={adminReplaceMediaInputRef}
                        type="file"
                        className="hidden"
                        accept="image/*,video/mp4,.mp4"
                        disabled={adminUploadingImage}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          const rawId = e.currentTarget.dataset.replaceForId;
                          delete e.currentTarget.dataset.replaceForId;
                          e.currentTarget.value = '';
                          const mediaId = Number(rawId);
                          if (!file || !adminSystemId || !Number.isFinite(mediaId)) return;
                          const isVideo = isCatalogVideoFile(file);
                          if (isVideo) {
                            setAdminUploadingImage(true);
                            try {
                              await replaceAdminCatalogSystemMedia(adminSystemId, mediaId, file);
                              await reloadProductGroup();
                              haptic.success();
                            } catch {
                              haptic.error();
                            } finally {
                              setAdminUploadingImage(false);
                            }
                            return;
                          }
                          void openCatalogMediaCrop(file, mediaId);
                        }}
                      />
                      <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-3 sm:gap-y-2 w-full min-w-0">
                        <button
                          type="button"
                          disabled={adminUploadingImage || adminGalleryAtCapacity}
                          onClick={() => {
                            haptic.selection();
                            adminAppendMediaInputRef.current?.click();
                          }}
                          className="min-h-[44px] shrink-0 self-start inline-flex items-center justify-center gap-2 text-[11px] font-semibold px-3 py-2 rounded-xl touch-manipulation disabled:opacity-45"
                          style={{ background: 'rgba(129,199,132,0.14)', color: '#B7F5CB', border: '1px solid rgba(129,199,132,0.45)' }}
                        >
                          {adminUploadingImage ? 'Загрузка...' : '➕ Медиа (фото / MP4)'}
                        </button>
                        <span
                          className="text-[10px] font-medium leading-snug flex flex-col gap-0.5 min-w-0 sm:max-w-[min(272px,calc(100vw-12rem))] flex-1"
                          style={{ color: 'var(--text-3)' }}
                          title={
                            adminHasLegacyPreviewTiles
                              ? `${adminVisibleMediaCount} кадр(ов) в карточке по URL; на сервере в медиатеке пока ${adminServerMediaCount}. Для лимита 10 учитываются кадры в карусели.`
                              : `Занято ${adminSlotsUsedForLimit} из ${CATALOG_MEDIA_LIMIT} слотов медиакаталога (по строкам или по карусели — что больше).`
                          }
                        >
                          <span>
                            занято{' '}
                            <span style={{ color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>
                              {adminSlotsUsedForLimit}/{CATALOG_MEDIA_LIMIT}
                            </span>
                            {adminSlotsFreeForLimit > 0 ? (
                              <span>{' · '}ещё можно {adminSlotsFreeForLimit}</span>
                            ) : (
                              <span style={{ color: '#FC814A' }}>{' '}лимит</span>
                            )}
                          </span>
                          {(adminHasLegacyPreviewTiles ||
                            adminVisibleMediaCount !== adminServerMediaCount) && (
                            <span className="opacity-90 leading-tight">
                              карусель {Math.min(CATALOG_MEDIA_LIMIT, adminVisibleMediaCount)} · в БД{' '}
                              {Math.min(CATALOG_MEDIA_LIMIT, adminServerMediaCount)}
                            </span>
                          )}
                        </span>
                        {hasRealCatalogMediaRows && (
                          <button
                            type="button"
                            disabled={adminDeletingImage}
                            onClick={async () => {
                              if (!adminSystemId) return;
                              if (!window.confirm('Удалить всю медиа-галерею этой системы?')) return;
                              haptic.selection();
                              setAdminDeletingImage(true);
                              try {
                                await deleteAdminCatalogSystemImage(adminSystemId);
                                await updateAdminGroupContent({ group_id: group.group_id, image_url: null });
                                await reloadProductGroup();
                                setFailedSlides(new Set());
                                setSlideIdx(0);
                                haptic.success();
                              } catch {
                                haptic.error();
                              } finally {
                                setAdminDeletingImage(false);
                              }
                            }}
                            className="min-h-[44px] shrink-0 self-start inline-flex items-center justify-center text-[11px] font-semibold px-3 py-2 rounded-xl touch-manipulation"
                            style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.28)' }}
                          >
                            {adminDeletingImage ? '…' : 'Очистить всё'}
                          </button>
                        )}
                      </div>
                      {adminGalleryList !== null &&
                        adminGalleryList.length === 0 &&
                        v2Gallery.some((m) => m.id < 0) && (
                        <div className="flex flex-col gap-2 mt-1">
                          <p className="text-[10px] leading-snug" style={{ color: 'var(--text-3)' }}>
                            Кадры только по старым ссылкам — в медиатеке пока нет записей. Можно{' '}
                            <strong style={{ color: 'var(--text-2)' }}>сохранить ссылки в базу как медиа</strong> этого
                            товара (без загрузки файлов заново), либо дозагрузить через «Медиа».
                          </p>
                          <button
                            type="button"
                            disabled={!adminSystemId || adminLinkingCarousel}
                            onClick={async () => {
                              if (!adminSystemId) return;
                              const imgs = slides.filter((s) => s.kind === 'image').map((s) => s.src.trim()).filter(Boolean);
                              if (!imgs.length) return;
                              const extra =
                                adminServerMediaCount > 0
                                  ? `\nУже есть ${adminServerMediaCount} записей — они будут удалены и заменены текущими кадрами карусели.`
                                  : '';
                              if (
                                !window.confirm(
                                  `Привязать ${imgs.length} кадр(ов) из карусели к каталогу (система #${adminSystemId}) и сохранить в базу?${extra}`,
                                )
                              ) {
                                return;
                              }
                              haptic.selection();
                              setAdminLinkingCarousel(true);
                              try {
                                await importAdminCatalogSystemMediaUrls(adminSystemId, {
                                  urls: imgs,
                                  replace: true,
                                });
                                await reloadProductGroup();
                                haptic.success();
                              } catch {
                                haptic.error();
                              } finally {
                                setAdminLinkingCarousel(false);
                              }
                            }}
                            className="min-h-[44px] self-start inline-flex items-center justify-center text-[11px] font-semibold px-3 py-2 rounded-xl disabled:opacity-40 touch-manipulation"
                            style={{
                              background: 'rgba(99,179,237,0.14)',
                              color: 'var(--cyan-bright)',
                              border: '1px solid var(--cyan-border)',
                            }}
                          >
                            {adminLinkingCarousel
                              ? 'Сохраняю…'
                              : 'Сохранить кадры карусели в медиатеку (ссылки в БД)'}
                          </button>
                        </div>
                      )}
                      {v2Gallery.length > 0 && (
                        <div
                          className="flex gap-3 overflow-x-auto pb-2 pt-0.5 -mx-2 px-2 scroll-smooth snap-x snap-mandatory [scrollbar-width:thin]"
                          style={{ scrollbarColor: 'rgba(255,255,255,0.2) transparent' }}
                        >
                          {v2Gallery.map((m, gi) => (
                            <div
                              key={m.id}
                              className="snap-start shrink-0 w-[clamp(106px,min(122px,calc((100vw-4rem)/2.15)),126px)] rounded-xl overflow-hidden flex flex-col gap-2 p-2.5 touch-manipulation"
                              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-subtle)' }}
                            >
                              <div
                                className="relative w-full rounded-lg overflow-hidden bg-black/40"
                                style={{ aspectRatio: '16 / 10' }}
                              >
                                {m.type === 'video' ? (
                                  <video
                                    src={resolveBackendMediaUrl(m.url)}
                                    muted
                                    playsInline
                                    preload="metadata"
                                    className="w-full h-full object-cover"
                                    aria-hidden
                                  />
                                ) : (
                                  <img src={resolveBackendMediaUrl(m.url)} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                                )}
                                {m.is_primary && m.type === 'image' && (
                                  <span className="absolute top-0.5 left-0.5 text-[8px] font-bold px-1 rounded" style={{ background: 'rgba(245,197,24,0.9)', color: '#0A192F' }}>обл</span>
                                )}
                              </div>
                              {isAdmin && adminSystemId && (
                                <>
                              <div className="flex justify-center gap-1">
                                <button
                                  type="button"
                                  aria-label="Выше"
                                  disabled={gi === 0 || adminUploadingImage || m.id < 0}
                                  onClick={() => moveGalleryItem(m.id, -1)}
                                  className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl active:opacity-80 touch-manipulation"
                                  style={{ color: 'var(--cyan-bright)', border: '1px solid var(--border-subtle)' }}
                                >
                                  <ChevronUp size={22} strokeWidth={2.25} />
                                </button>
                                <button
                                  type="button"
                                  aria-label="Ниже"
                                  disabled={gi >= v2Gallery.length - 1 || adminUploadingImage || m.id < 0}
                                  onClick={() => moveGalleryItem(m.id, 1)}
                                  className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl active:opacity-80 touch-manipulation"
                                  style={{ color: 'var(--cyan-bright)', border: '1px solid var(--border-subtle)' }}
                                >
                                  <ChevronDown size={22} strokeWidth={2.25} />
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-1 justify-stretch [&>button]:min-h-[42px] [&>button]:flex-1 [&>button]:basis-[30%]">
                                {m.type === 'image' && (
                                  <button
                                    type="button"
                                    title="Обложка каталога"
                                    disabled={adminUploadingImage || m.id < 0}
                                    onClick={async () => {
                                      if (!adminSystemId) return;
                                      haptic.selection();
                                      try {
                                        await setAdminCatalogSystemMediaPrimary(adminSystemId, m.id);
                                        await reloadProductGroup();
                                        haptic.success();
                                      } catch {
                                        haptic.error();
                                      }
                                    }}
                                    className="text-[12px] font-bold px-2 py-1.5 rounded-lg touch-manipulation"
                                    style={{
                                      background: m.is_primary ? 'rgba(245,197,24,0.2)' : 'rgba(255,255,255,0.06)',
                                      color: m.is_primary ? '#F5C518' : 'var(--text-3)',
                                      border: '1px solid var(--border-subtle)',
                                    }}
                                  >
                                    ★
                                  </button>
                                )}
                                {m.type === 'video' && (
                                  <button
                                    type="button"
                                    title="Скачать MP4"
                                    disabled={adminDownloadingMediaId === m.id || m.id < 0}
                                    onClick={async () => {
                                      haptic.selection();
                                      setAdminDownloadingMediaId(m.id);
                                      try {
                                        await downloadBackendMediaFile(m.url, { mediaId: m.id, kind: 'video' });
                                        haptic.success();
                                      } catch {
                                        haptic.error();
                                      } finally {
                                        setAdminDownloadingMediaId(null);
                                      }
                                    }}
                                    className="text-[12px] font-bold px-2 py-1.5 rounded-lg touch-manipulation inline-flex items-center justify-center"
                                    style={{ background: 'rgba(99,179,237,0.14)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
                                  >
                                    {adminDownloadingMediaId === m.id ? (
                                      <span className="text-[10px]">…</span>
                                    ) : (
                                      <Download size={15} strokeWidth={2.25} />
                                    )}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  title={m.type === 'video' ? 'Заменить MP4' : 'Заменить / кадр'}
                                  disabled={adminUploadingImage || m.id < 0}
                                  onClick={() => {
                                    haptic.selection();
                                    triggerReplaceMediaPick(m);
                                  }}
                                  className="text-[13px] font-bold px-2 py-1.5 rounded-lg touch-manipulation"
                                  style={{ background: 'rgba(99,179,237,0.12)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
                                >
                                  ↻
                                </button>
                                <button
                                  type="button"
                                  title="Удалить"
                                  disabled={adminDeletingImage || m.id < 0}
                                  onClick={async () => {
                                    if (!adminSystemId || m.id <= 0) return;
                                    haptic.selection();
                                    setAdminDeletingImage(true);
                                    try {
                                      await deleteAdminCatalogSystemMediaItem(adminSystemId, m.id);
                                      await reloadProductGroup();
                                      setFailedSlides(new Set());
                                      setSlideIdx(0);
                                      haptic.success();
                                    } catch {
                                      haptic.error();
                                    } finally {
                                      setAdminDeletingImage(false);
                                    }
                                  }}
                                  className="text-[16px] leading-none font-bold px-2 py-1.5 rounded-lg touch-manipulation"
                                  style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.28)' }}
                                >
                                  ×
                                </button>
                              </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                ) : (
                  <span className="text-[11px] px-2.5 py-1 rounded-lg" style={{ background: 'rgba(246,173,85,0.12)', color: '#F6AD55', border: '1px solid rgba(246,173,85,0.35)' }}>
                    Фото станет доступно после привязки system
                  </span>
                )}
                {adminSystemId && !hasRealCatalogMediaRows && group.image_url && (
                  <button
                    type="button"
                    disabled={adminDeletingImage}
                    onClick={async () => {
                      if (!adminSystemId) return;
                      setAdminDeletingImage(true);
                      try {
                        await deleteAdminCatalogSystemImage(adminSystemId);
                        await updateAdminGroupContent({ group_id: group.group_id, image_url: null });
                        setGroup((prev) => (prev ? { ...prev, image_url: undefined } : prev));
                        setFailedSlides(new Set());
                        setSlideIdx(0);
                        haptic.success();
                      } catch {
                        haptic.error();
                      } finally {
                        setAdminDeletingImage(false);
                      }
                    }}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-lg"
                    style={{ background: 'rgba(252,129,74,0.14)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.35)' }}
                  >
                    {adminDeletingImage ? 'Удаляю...' : '🗑 Удалить фото'}
                  </button>
                )}
              </div>
          </div>
        )}
        <p className="text-[11px] mt-1.5 transition-all duration-300" style={{ color: 'var(--text-3)' }}>
          {categoryHeroNote}
        </p>

        {showPimSubtypeNav && pimUi ? (
          <PimSubtypeNavigator
            sectionTitle={pimUi.sectionTitle}
            tiles={visiblePimNavTiles(pimUi.navTiles, siblingGroups)}
            siblings={siblingGroups}
            activeGroupId={effectiveGroupId}
            onNavigate={(gid) => navigate(toGroupUrl(gid))}
            hapticSelection={() => haptic.selection()}
          />
        ) : isRulonnye && siblingGroups.length > 0 ? (
          <RulonnyeTypeNavigator
            siblings={siblingGroups}
            selection={rulonNav}
            activeGroupId={effectiveGroupId}
            onNavigate={(gid) => navigate(toGroupUrl(gid))}
            onSelectionChange={setRulonNav}
            hapticSelection={() => haptic.selection()}
            navCopy={rulonNavCopy}
          />
        ) : !isPimBranch && siblingGroups.length > 1 ? (
          <SiblingGroupTileNavigator
            sectionTitle={(() => {
              const cat = (group?.category || '').toLowerCase();
              if (cat.includes('карниз')) return 'Выберите тип системы';
              if (cat.includes('рулон')) return 'Выберите тип шторы';
              if (cat.includes('жалюзи')) return 'Выберите тип жалюзи';
              if (cat.includes('маркиза')) return 'Выберите тип маркизы';
              return 'Выберите систему';
            })()}
            items={siblingGroups
              .filter((sg) => Number(sg.variants_count ?? 0) > 0)
              .map((sg) => ({
              group_id: sg.group_id,
              name: sg.name,
              hint: resolveConfiguratorTileHint({
                tile_hint: sg.tile_hint,
                variants_count: sg.variants_count,
              }),
              disabled: false,
            }))}
            activeGroupId={effectiveGroupId}
            onNavigate={(gid) => navigate(toGroupUrl(gid))}
            hapticSelection={() => haptic.selection()}
          />
        ) : null}

        {!isRulonnye && !isPimBranch && subtypeSections.length > 0 && (
          <div className="mt-2 space-y-2">
            {subtypeSections.map((sec) => (
              <div key={sec.section}>
                <p className="text-[10px] mb-1" style={{ color: 'var(--text-3)' }}>{sec.section}</p>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {sec.items.map((item) => (
                    <button
                      key={`${sec.section}-${item.key}`}
                      type="button"
                      onClick={() => {
                        if (item.group_id === effectiveGroupId) return;
                        haptic.selection();
                        navigate(toGroupUrl(item.group_id));
                      }}
                      className="press shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
                      style={item.group_id === effectiveGroupId || item.active
                        ? {
                            background: 'rgba(99,179,237,0.2)',
                            color: 'var(--cyan-bright)',
                            border: '1px solid var(--cyan-border)',
                          }
                        : {
                            background: 'rgba(255,255,255,0.05)',
                            color: 'var(--text-2)',
                            border: '1px solid var(--border-subtle)',
                          }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-end justify-between mt-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold mb-0.5" style={{ color: 'var(--text-3)' }}>
              {displayPrice !== minPrice ? 'Розница' : 'от'}
            </p>
            <p
              className="text-2xl sm:text-3xl font-black tabular-nums leading-none"
              style={{ color: 'var(--cyan-bright)', ...premiumPriceRevealStyle }}
            >
              {Math.round(animatedCatalogPrice).toLocaleString('ru-RU')}
              <span className="text-lg font-semibold ml-1" style={{ color: 'var(--text-2)' }}>₽</span>
            </p>
            {isAdmin && minPrice > 0 && Math.round(displayPrice) !== Math.round(minPrice) && (
              <p className="text-[10px] mt-1 leading-tight" style={{ color: 'var(--text-3)' }}>
                в каталоге от {Math.round(minPrice).toLocaleString('ru-RU')} ₽
              </p>
            )}
            {v2PriceLoading && (
              <p className="text-[10px] mt-1" style={{ color: 'var(--text-3)' }}>Пересчет...</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-xs" style={{ color: 'var(--text-3)' }}>{group.variants.length} вариантов</p>
            {activeVariant?.motor_model && (
              <p className="text-[10px] font-mono mt-0.5 px-2 py-0.5 rounded-lg"
                style={{ background: 'var(--sapphire-card)', color: 'var(--cyan)', border: '1px solid var(--border-glass)' }}>
                {activeVariant.motor_model}
              </p>
            )}
          </div>
        </div>
      </div>

      <div
        className="flex-1 mt-3 space-y-2.5"
        style={{
          paddingBottom: showCheckoutDock
            ? 'var(--app-scroll-pad-with-checkout)'
            : 'var(--app-scroll-pad-bottom)',
        }}
      >

        {/* ── CONFIGURATOR ── */}
        <div className="glass-light px-3.5 py-3.5 sm:px-4 sm:py-4">
          <p className="text-[10px] font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--cyan)' }}>◈ Конфигурация</p>
          {validationHint && (
            <div className="mb-3 px-3 py-2 rounded-xl text-xs font-semibold"
              style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.35)' }}>
              {validationHint}
            </div>
          )}

          {/* Step 0: Subtype — in-place reload, no navigation */}
          {/* Hidden for V3 categories — sibling tabs above serve as navigation */}
          {(() => {
            if (siblingGroups.length >= 2 || isRulonnye || isPimBranch) return null;
            const COLOR_WORDS = ['белый', 'черный', 'чёрный', 'серый', 'серебристый', 'premium', 'премиум', 'белое золото', 'золотой', 'матовый', 'глянцевый'];
            const subtypeItems = catalogSubtypes.filter(({ subtype }) => {
              const s = subtype.toLowerCase();
              return !COLOR_WORDS.some((c) => s === c || s.startsWith(c));
            });
            if (subtypeItems.length < 2) return null;
            return (
          <div className="mb-5">
            <p className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color: 'var(--text-2)' }}>
              <Sparkles size={12} color="var(--cyan)" /> {(() => {
                const cat = (group?.category || '').toLowerCase();
                if (cat.includes('карниз')) return 'Тип карниза';
                if (cat.includes('рулон')) return 'Тип шторы';
                if (cat.includes('жалюзи')) return 'Тип жалюзи';
                if (cat.includes('маркиза')) return 'Тип маркизы';
                if (cat.includes('экран')) return 'Тип экрана';
                return 'Тип системы';
              })()}
            </p>
            <div className="flex flex-wrap gap-2">
              {subtypeItems.map(({ subtype, slug, min_price }) => {
                const isActive = selectedSubtype === subtype || (!selectedSubtype && (group?.subcategory || '') === subtype);
                return (
                  <button
                    key={slug}
                    disabled={subtypeLoading}
                    onClick={async () => {
                      if (isActive) return;
                      haptic.selection();
                      setSelectedSubtype(subtype);
                      setSubtypeLoading(true);
                      // Reset configurator state for new subtype
                      setSelectedBattery(null);
                      setSelectedIfaceType(null);
                      setSelectedProtocol(null);
                      setSelectedColor(null);
                      setSelectedDim(null);
                      setCustomDimInput('');
                      setClientHeightM('');
                      setSelectedOpening(null);
                      setCfgTouched({
                        tier: false, power: false, opening: false, iface: false, protocol: false,
                        control: false, color: false, profile_color: false, motor_color: false,
                      });
                      // v2Price cleared via hook on group change
                      setSlideIdx(0);
                      setFailedSlides(new Set());
                      try {
                        const loaded = await getGroupById(`v2::${slug}`);
                        const gallery = parseV2SystemMedia((loaded as any)?.gallery);
                        setGroup({
                          ...loaded,
                          gallery: gallery.length ? gallery : undefined,
                        });
                        // Update URL without React Router navigation (prevents useEffect re-run)
                        const newUrl = toGroupUrl(`v2::${slug}`);
                        window.history.replaceState(null, '', newUrl);
                        if (isAdmin) {
                          setAdminGalleryList(null);
                        }
                        // CatalogSystem + медиа подтягивает useEffect(... adminLookupKey / systemSlug ...)
                        // после обновления group.group_id — без дублирования с ручным resolve (гонки между подтипами).
                      } catch {
                        /* keep current group */
                      } finally {
                        setSubtypeLoading(false);
                      }
                    }}
                    className="press flex flex-col items-start px-3 py-2 rounded-xl text-xs font-semibold transition-all"
                    style={isActive ? {
                      background: 'rgba(99,179,237,0.18)',
                      border: '1px solid rgba(99,179,237,0.5)',
                      color: 'var(--cyan-bright)',
                      boxShadow: '0 0 10px rgba(99,179,237,0.15)',
                    } : {
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid var(--border-subtle)',
                      color: 'var(--text-2)',
                    }}
                  >
                    {subtypeLoading && isActive
                      ? <span style={{ color: 'var(--cyan)' }}>…</span>
                      : <span>{subtype}</span>}
                    {min_price != null && (
                      <span className="text-[10px] font-normal mt-0.5" style={{ color: isActive ? 'var(--cyan)' : 'var(--text-3)' }}>
                        от {Math.round(min_price).toLocaleString('ru-RU')} ₽
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
            );
          })()}

          {/* Tier: Standard / Premium */}
          {showTierChoice && (!schemaSteps || schemaSteps.includes('tier')) && (
            <ConfiguratorReveal show stepKey="tier" schema={stepCopySchema} className="mb-5">
              <div className={CFG_CHIP_ROW} ref={tierBlockRef}>
                {availableTiers.map((tier) => {
                  const label = tier.toLowerCase() === 'premium' ? 'Premium' : tier.toLowerCase() === 'standard' ? 'Standard' : tier;
                  const isActive = cfgTouched.tier && selectedTierSlug === tier;
                  const disabled = isChipDisabled('tier', tier);
                  return (
                    <button key={tier} type="button" disabled={disabled}
                      onClick={() => {
                        const norm = normalizeCatalogTierSlug(tier);
                        if (group && isRazdvizhnyeBranch(group) && norm && showTierChoice) {
                          if (norm === catalogTier) return;
                          haptic.selection();
                          const gid = String(effectiveGroupId || group.group_id || '').trim();
                          if (gid) navigate(toGroupUrl(gid, norm), { replace: true });
                          return;
                        }
                        setCfgTouched((p) => ({ ...p, tier: true }));
                        setSelectedTierSlug(isActive ? null : tier);
                        haptic.selection();
                      }}
                      className={CFG_CHIP_TIER}
                      style={chipAccentStyle(tierAccentKey(tier), isActive)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </ConfiguratorReveal>
          )}

          <ConfiguratorReveal show={cfgReveal.showPower} stepKey="power" schema={stepCopySchema}>
            {dualPowerChoice && (
              <div className={CFG_CHIP_ROW}>
                {[
                  { val: false as boolean, label: '220В сеть', icon: '⚡', accent: 'power-wired' as const },
                  { val: true as boolean, label: 'АКБ', icon: '🔋', accent: 'power-battery' as const },
                ].map(({ val, label, icon, accent }) => {
                  const isActive = cfgTouched.power && selectedBattery === val;
                  return (
                  <button key={String(val)}
                    onClick={() => {
                      setCfgTouched((p) => ({ ...p, power: true }));
                      setSelectedBattery(selectedBattery === val ? null : val);
                      haptic.selection();
                    }}
                    className={CFG_CHIP_PRIMARY}
                    style={chipAccentStyle(accent, isActive)}
                  >
                    <span className="text-xl" aria-hidden>{icon}</span> {label}
                  </button>
                  );
                })}
              </div>
            )}
            {wiredOnlyPower && (
              <button
                type="button"
                onClick={() => {
                  setCfgTouched((p) => ({ ...p, power: true }));
                  setSelectedBattery(false);
                  haptic.selection();
                }}
                className={CFG_CHIP_POWER_SOLO}
                style={chipAccentStyle('power-wired', cfgTouched.power && selectedBattery === false)}
              >
                <span className="text-3xl" aria-hidden>⚡</span>
                <span>220В сеть</span>
              </button>
            )}
          </ConfiguratorReveal>

          <ConfiguratorReveal show={cfgReveal.showOpening && isSliding} stepKey="opening" schema={stepCopySchema}>
              <motion.div layout className={CFG_CHIP_OPENING_GRID}>
                {OPENING_TYPES.filter((o) => o.val !== 'center').map(({ val, label, icon }) => {
                  const isActive = cfgTouched.opening && selectedOpening === val;
                  return (
                    <motion.button
                      key={val}
                      layout
                      type="button"
                      onClick={() => {
                        setCfgTouched((p) => ({ ...p, opening: true }));
                        setSelectedOpening(isActive ? null : val);
                        haptic.selection();
                      }}
                      className={`${CFG_CHIP_TILE} w-full ${CFG_CHIP_TRANSITION}`}
                      style={chipAccentStyle('opening', isActive)}
                    >
                      <OpeningDirectionIcon kind={icon} />
                      <span>{label}</span>
                    </motion.button>
                  );
                })}
                {OPENING_TYPES.filter((o) => o.val === 'center').map(({ val, label, icon }) => {
                  const isActive = cfgTouched.opening && selectedOpening === val;
                  return (
                    <motion.button
                      key={val}
                      layout
                      type="button"
                      onClick={() => {
                        setCfgTouched((p) => ({ ...p, opening: true }));
                        setSelectedOpening(isActive ? null : val);
                        haptic.selection();
                      }}
                      className={`${CFG_CHIP_TILE} ${CFG_CHIP_OPENING_CENTER} w-full ${CFG_CHIP_TRANSITION}`}
                      style={mergeChipGridStyle(chipAccentStyle('opening', isActive), { style: { gridColumn: '1 / -1' } })}
                    >
                      <OpeningDirectionIcon kind={icon} />
                      <span>{label}</span>
                    </motion.button>
                  );
                })}
              </motion.div>
          </ConfiguratorReveal>

          {useControlStep && (availableControlTags.length > 0 || visibleProtocolGroups.length > 0 || availableProtocols.length > 0) && (
            <ConfiguratorReveal show={cfgReveal.showControl} stepKey="control" schema={stepCopySchema} className="mb-5">
            <div ref={controlBlockRef}>
              {availableControlTags.length > 0 && visibleProtocolGroups.length === 0 && (
                <div className="mb-3">
                  <p className="text-[10px] mb-1.5" style={{ color: 'var(--text-3)' }}>Опции (можно несколько)</p>
                  <ConfiguratorChipGrid>
                    {availableControlTags.map((tag) => {
                      const isActive = selectedControlTags.includes(tag);
                      const disabled = isChipDisabled('control', tag);
                      const hint = controlDisableReason('control', tag);
                      return (
                        <motion.button
                          key={tag}
                          layout
                          type="button"
                          disabled={disabled}
                          title={hint ?? undefined}
                          onClick={() => { toggleControlTag(tag); haptic.selection(); }}
                          className={CFG_CHIP_TAG}
                          style={isActive
                            ? { background: 'rgba(99,179,237,0.2)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan)' }
                            : { background: 'rgba(255,255,255,0.04)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }
                          }
                        >
                          {tag}
                        </motion.button>
                      );
                    })}
                  </ConfiguratorChipGrid>
                </div>
              )}
              {visibleProtocolGroups.length > 0 ? visibleProtocolGroups.map((pg) => (
                <div key={pg.id} className="mb-3">
                  <p className="text-[10px] mb-1.5 font-medium" style={{ color: 'var(--text-3)' }}>
                    {protocolGroupLabel(pg)}
                  </p>
                  <ConfiguratorChipGrid>
                    {pg.options.filter((proto) => proto !== 'Wi-Fi (MiHome)').map((proto, index, arr) => {
                      const theme = PROTOCOL_THEME[proto];
                      const isWireless = pg.exclusive && (pg.id === 'wireless' || pg.id === 'smart_home');
                      const isActive = isWireless
                        ? cfgTouched.protocol && selectedProtocol === proto
                        : selectedControlTags.includes(proto);
                      const span = cfgChipGridSpan(index, arr.length, isActive, { activeFullWidth: isWireless });
                      const kind = isWireless ? 'protocol' as const : 'control' as const;
                      const disabled = isChipDisabled(kind, proto, pg.id);
                      const hint = controlDisableReason(kind, proto, pg.id);
                      const accent = isActive
                        ? (isWireless
                          ? {
                              ...chipAccentStyle('protocol', true),
                              background: theme?.bg ?? chipAccentStyle('protocol', true).background,
                              color: theme?.text ?? chipAccentStyle('protocol', true).color,
                              border: `1.5px solid ${theme?.border ?? 'var(--cyan)'}`,
                            }
                          : chipAccentStyle('control', true))
                        : chipAccentStyle(isWireless ? 'protocol' : 'control', false);
                      return (
                        <motion.button
                          key={`${pg.id}-${proto}`}
                          layout
                          type="button"
                          disabled={disabled}
                          title={hint ?? undefined}
                          onClick={() => {
                            if (isWireless) selectProtocolInGroup(proto, pg);
                            else toggleControlTag(proto);
                            haptic.selection();
                          }}
                          className={`${CFG_CHIP_TILE} ${span.className}`}
                          style={mergeChipGridStyle(accent, span)}
                        >
                          <span className="text-lg shrink-0" aria-hidden>{theme?.icon ?? (isWireless ? '📡' : '🔌')}</span>
                          <span className="text-center leading-tight">{theme?.label ?? proto}</span>
                        </motion.button>
                      );
                    })}
                  </ConfiguratorChipGrid>
                </div>
              )) : (
                <ConfiguratorChipGrid>
                  {availableProtocols.map((proto, index, arr) => {
                    const theme = PROTOCOL_THEME[proto];
                    const isActive = cfgTouched.protocol && selectedProtocol === proto;
                    const span = cfgChipGridSpan(index, arr.length, isActive);
                    const accent = isActive
                      ? {
                          background: theme?.bg ?? 'rgba(99,179,237,0.2)',
                          color: theme?.text ?? 'var(--cyan-bright)',
                          border: `1.5px solid ${theme?.border ?? 'var(--cyan)'}`,
                        }
                      : chipAccentStyle('protocol', false);
                    return (
                      <motion.button
                        layout
                        key={proto}
                        type="button"
                        onClick={() => {
                          setCfgTouched((p) => ({ ...p, control: true, protocol: true }));
                          setSelectedProtocol(isActive ? null : proto);
                          haptic.selection();
                        }}
                        className={`${CFG_CHIP_TILE} ${span.className}`}
                        style={mergeChipGridStyle(accent, span)}
                      >
                        <span className="text-lg shrink-0" aria-hidden>{theme?.icon ?? '📡'}</span>
                        <span>{theme?.label ?? proto}</span>
                      </motion.button>
                    );
                  })}
                </ConfiguratorChipGrid>
              )}
              {matchAmbiguous && (
                <p className="text-[10px] mt-2" style={{ color: '#F6AD55' }}>Уточните управление — найдено несколько вариантов с близкой ценой.</p>
              )}
            </div>
            </ConfiguratorReveal>
          )}

          {/* Step 3: Interface type */}
          {cfgReveal.showControl && showLegacyIface && (
            <div className="mb-5" ref={ifaceBlockRef}>
              <p className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color: 'var(--text-2)' }}>
                <Shield size={12} color="var(--cyan)" /> Тип подключения
                {stepState.nextKey === 'iface' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(99,179,237,0.18)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}>
                    Следующий шаг
                  </span>
                )}
              </p>
              <ConfiguratorChipGrid>
                <motion.button
                  layout
                  type="button"
                  onClick={() => {
                    setCfgTouched((p) => ({ ...p, iface: true }));
                    setSelectedIfaceType(null);
                    setSelectedProtocol(null);
                    setSelectedColor(null);
                    haptic.selection();
                  }}
                  className={`${CFG_CHIP_TAG} ${cfgChipSpanClass(cfgTouched.iface && selectedIfaceType === null)}`}
                  style={chipAccentStyle('control', cfgTouched.iface && selectedIfaceType === null)}
                >
                  Любой
                </motion.button>
                {availableIfaceTypes.map((iface) => {
                  const info = IFACE_INFO[iface];
                  const isActive = cfgTouched.iface && selectedIfaceType === iface;
                  return (
                    <motion.button
                      key={iface}
                      layout
                      type="button"
                      onClick={() => {
                        setCfgTouched((p) => ({ ...p, iface: true }));
                        setSelectedIfaceType(isActive ? null : iface);
                        setSelectedProtocol(null);
                        setSelectedColor(null);
                        haptic.selection();
                      }}
                      className={`${CFG_CHIP_TILE} ${cfgChipSpanClass(isActive)}`}
                      style={chipAccentStyle('control', isActive)}
                    >
                      <span className="text-lg" aria-hidden>{info?.icon ?? '◽'}</span>
                      <span>{info?.label ?? iface}</span>
                    </motion.button>
                  );
                })}
              </ConfiguratorChipGrid>
            </div>
          )}

          {/* Step 4: Protocol */}
          {showLegacyProtocol && (
            <div className="mb-5" ref={protocolBlockRef}>
              <p className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color: 'var(--text-2)' }}>
                <Wifi size={12} color="var(--cyan)" /> Протокол управления
                {stepState.nextKey === 'protocol' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(99,179,237,0.18)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}>
                    Следующий шаг
                  </span>
                )}
              </p>
              <ConfiguratorChipGrid>
                <motion.button
                  layout
                  type="button"
                  onClick={() => {
                    setCfgTouched((p) => ({ ...p, protocol: true }));
                    setSelectedProtocol(null);
                    haptic.selection();
                  }}
                  className={`${CFG_CHIP_TAG} ${cfgChipSpanClass(cfgTouched.protocol && selectedProtocol === null)}`}
                  style={chipAccentStyle('protocol', cfgTouched.protocol && selectedProtocol === null)}
                >
                  Любой
                </motion.button>
                {cfgChipSortActiveFirst(
                  availableProtocols,
                  (proto) => cfgTouched.protocol && selectedProtocol === proto,
                ).map((proto) => {
                  const isActive = cfgTouched.protocol && selectedProtocol === proto;
                  const theme = PROTOCOL_THEME[proto];
                  return (
                    <motion.button
                      key={proto}
                      layout
                      type="button"
                      onClick={() => {
                        setCfgTouched((p) => ({ ...p, protocol: true }));
                        setSelectedProtocol(isActive ? null : proto);
                        haptic.selection();
                      }}
                      className={`${CFG_CHIP_TILE} ${cfgChipSpanClass(isActive)}`}
                      style={isActive
                        ? {
                            background: theme?.bg ?? 'rgba(99,179,237,0.2)',
                            color: theme?.text ?? 'var(--cyan-bright)',
                            border: `1.5px solid ${theme?.border ?? 'var(--cyan)'}`,
                          }
                        : chipAccentStyle('protocol', false)}
                    >
                      <span className="text-lg shrink-0" aria-hidden>{theme?.icon ?? (proto.startsWith('Wi-Fi') ? '📶' : '⚡')}</span>
                      <span className="text-center leading-tight">{theme?.label ?? proto}</span>
                    </motion.button>
                  );
                })}
              </ConfiguratorChipGrid>
            </div>
          )}

          <ConfiguratorReveal show={cfgReveal.showColors} stepKey="profile_color" schema={stepCopySchema}>
            {availableProfileColors.length > 0 && (!schemaSteps || schemaSteps.includes('profile_color')) && (
              <div className="mb-4" ref={profileColorBlockRef}>
                <p className="text-[10px] font-medium mb-2" style={{ color: 'var(--text-3)' }}>Цвет профиля</p>
                <ConfiguratorChipGrid>
                  {cfgChipSortActiveFirst(
                    availableProfileColors,
                    (color) => cfgTouched.profile_color && selectedProfileColor === color,
                  ).map((color, index, arr) => {
                    const isActive = cfgTouched.profile_color && selectedProfileColor === color;
                    const disabled = isChipDisabled('profile', color);
                    const span = cfgChipGridSpan(index, arr.length, isActive);
                    return (
                      <motion.button
                        key={color}
                        layout
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                          setCfgTouched((p) => ({ ...p, profile_color: true }));
                          setSelectedProfileColor(isActive ? null : color);
                          haptic.selection();
                        }}
                        className={`${CFG_CHIP_COLOR} ${span.className}`}
                        style={mergeChipGridStyle(chipAccentStyle(colorAccentKey(color), isActive), span)}
                      >
                        {color}
                      </motion.button>
                    );
                  })}
                </ConfiguratorChipGrid>
              </div>
            )}
            {availableMotorColors.length > 0 && (!schemaSteps || schemaSteps.includes('motor_color')) && (
              <div ref={motorColorBlockRef}>
                <p className="text-[10px] font-medium mb-2" style={{ color: 'var(--text-3)' }}>Цвет мотора</p>
                <ConfiguratorChipGrid>
                  {cfgChipSortActiveFirst(
                    availableMotorColors,
                    (color) => cfgTouched.motor_color && selectedMotorColor === color,
                  ).map((color, index, arr) => {
                    const isActive = cfgTouched.motor_color && selectedMotorColor === color;
                    const disabled = isChipDisabled('motor', color);
                    const span = cfgChipGridSpan(index, arr.length, isActive);
                    return (
                      <motion.button
                        key={color}
                        layout
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                          setCfgTouched((p) => ({ ...p, motor_color: true }));
                          setSelectedMotorColor(isActive ? null : color);
                          haptic.selection();
                        }}
                        className={`${CFG_CHIP_COLOR} ${span.className}`}
                        style={mergeChipGridStyle(chipAccentStyle(colorAccentKey(color), isActive), span)}
                      >
                        {color}
                      </motion.button>
                    );
                  })}
                </ConfiguratorChipGrid>
              </div>
            )}
          </ConfiguratorReveal>

          {/* Step 5: Color (legacy single) */}
          {cfgReveal.showColors && availableColors.length > 0 && (!schemaSteps || schemaSteps.includes('color')) && !schemaSteps?.includes('profile_color') && (
            <div className="mb-5" ref={colorBlockRef}>
              <p className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color: 'var(--text-2)' }}>
                <Palette size={12} color="var(--cyan)" /> Цвет исполнения
                {stepState.nextKey === 'color' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(99,179,237,0.18)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}>
                    Следующий шаг
                  </span>
                )}
              </p>
              <ConfiguratorChipGrid>
                <motion.button
                  layout
                  type="button"
                  onClick={() => {
                    setCfgTouched((p) => ({ ...p, color: true }));
                    setSelectedColor(null);
                    haptic.selection();
                  }}
                  className={`${CFG_CHIP_TAG} ${cfgChipSpanClass(cfgTouched.color && selectedColor === null)}`}
                  style={chipAccentStyle('control', cfgTouched.color && selectedColor === null)}
                >
                  Любой
                </motion.button>
                {cfgChipSortActiveFirst(
                  availableColors,
                  (color) => cfgTouched.color && selectedColor === color,
                ).map((color, index, arr) => {
                  const isActive = cfgTouched.color && selectedColor === color;
                  const span = cfgChipGridSpan(index + 1, arr.length + 1, isActive);
                  return (
                    <motion.button
                      key={color}
                      layout
                      type="button"
                      onClick={() => {
                        setCfgTouched((p) => ({ ...p, color: true }));
                        setSelectedColor(isActive ? null : color);
                        haptic.selection();
                      }}
                      className={`${CFG_CHIP_COLOR} ${span.className}`}
                      style={mergeChipGridStyle(chipAccentStyle(colorAccentKey(color), isActive), span)}
                    >
                      <span
                        className="inline-block w-2 h-2 rounded-full mr-1.5 shrink-0"
                        style={{
                          background: color.toLowerCase().includes('бел') ? '#eee' : color.toLowerCase().includes('черн') ? '#222' : color.toLowerCase().includes('сер') ? '#888' : '#ccc',
                          border: '1px solid rgba(255,255,255,0.2)',
                        }}
                        aria-hidden
                      />
                      {color}
                    </motion.button>
                  );
                })}
              </ConfiguratorChipGrid>
            </div>
          )}

          {activeVariant?.motor_full_name && (
            <div className="mt-1 pt-3 flex items-center justify-between" style={{ borderTop: '1px solid var(--border-subtle)' }}>
              <p className="text-[11px]" style={{ color: 'var(--text-3)' }}>Модель мотора</p>
              <p className="text-[11px] font-mono font-semibold px-2 py-0.5 rounded-lg"
                style={{ background: 'var(--sapphire-card)', color: 'var(--cyan)', border: '1px solid var(--border-glass)' }}>
                {activeVariant.motor_full_name}
              </p>
            </div>
          )}

          <ConfiguratorReveal
            show={cfgReveal.showSize && matrixLayout.mode !== 'none'}
            stepKey="size"
            schema={stepCopySchema}
            className="mt-4"
          >
          <div ref={sizeBlockRef}>
            {stepState.nextKey === 'size' && (
              <p className="text-[10px] mb-2" style={{ color: 'var(--cyan-bright)' }}>
                {matrixLayout.mode === '2d'
                  ? 'Ширина и высота: сетка из прайса или свой замер в полях ниже'
                  : 'Следующий шаг: выберите размер'}
              </p>
            )}
            <ConfiguratorSizeCascade
              matrix={matrix}
              layout={matrixLayout}
              primaryLabel={dimLabels.primary}
              secondaryLabel={dimLabels.secondary}
              selectedPrimary={selectedDim}
              selectedSecondary={selectedSecondDim}
              onSelectPrimary={setSelectedDim}
              onSelectSecondary={setSelectedSecondDim}
              customDimInput={customDimInput}
              onCustomDimInput={setCustomDimInput}
              customSecondaryDimInput={customSecondaryDimInput}
              onCustomSecondaryDimInput={setCustomSecondaryDimInput}
              dimensionQuote={dimensionQuote}
              showRoomHeightNote={showRoomHeightNote}
              clientHeightM={clientHeightM}
              onClientHeightM={setClientHeightM}
              hapticSelection={() => haptic.selection()}
            />
          </div>
          </ConfiguratorReveal>

        </div>

        {/* ── LIVE PRICE BREAKDOWN ── */}
        {hasV2Breakdown && (
          <div className="glass-light px-3.5 py-3.5 sm:px-4 sm:py-4">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--cyan)' }}>◈ Структура цены</p>
            <div className="space-y-1.5">
              {v2BasePrice !== null && (
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: 'var(--text-3)' }}>Базовая цена</span>
                  <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{v2BasePrice.toLocaleString('ru-RU')} ₽</span>
                </div>
              )}
              {v2Surcharges.map((s) => (
                <div key={`${s.code}-${s.label}`} className="flex items-center justify-between text-sm">
                  <span style={{ color: 'var(--text-3)' }}>{s.label}</span>
                  <span style={{ color: 'var(--cyan-bright)', fontWeight: 600 }}>+{s.amount.toLocaleString('ru-RU')} ₽</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── QUICK SUMMARY ── */}
        {(groupShortCopy || isAdmin) && (
          <div className="glass-light px-3.5 py-3.5 sm:px-4 sm:py-4">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--cyan)' }}>◈ Кратко</p>
            {isAdmin && (
              <div className="mb-2">
                {!adminEditingDesc ? (
                  <button
                    type="button"
                    onClick={() => {
                      setAdminDescDraft(
                        groupShortCopy || String(group.short_description || group.short_summary || ''),
                      );
                      setAdminEditingDesc(true);
                      haptic.selection();
                    }}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-lg"
                    style={{ background: 'rgba(99,179,237,0.12)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
                  >
                    ✏️ Редактировать краткое описание
                  </button>
                ) : (
                  <div className="rounded-xl p-2.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}>
                    <textarea
                      value={adminDescDraft}
                      onChange={(e) => setAdminDescDraft(e.target.value)}
                      rows={3}
                      className="w-full rounded-lg p-2 text-xs"
                      style={{ background: 'rgba(10,25,47,0.8)', color: 'var(--text-1)', border: '1px solid var(--border-subtle)' }}
                      placeholder="1–2 предложения для блока «Кратко» (ниже на странице)…"
                    />
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        disabled={adminSavingDesc}
                        onClick={async () => {
                          setAdminSavingDesc(true);
                          try {
                            const trimmed = adminDescDraft.trim();
                            await persistAdminProductGroupCopy({
                              adminSystemId,
                              group,
                              effectiveGroupId,
                              catalogTier,
                              patch: { short_description: trimmed },
                            });
                            setGroup((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    short_description: trimmed,
                                    marketing_description: trimmed,
                                    short_summary: trimmed,
                                  }
                                : prev,
                            );
                            setAdminDescDraft(trimmed);
                            setAdminEditingDesc(false);
                            await reloadProductGroup({ preserveShort: trimmed });
                            haptic.success();
                          } catch (e) {
                            haptic.error();
                            window.alert(
                              'Ошибка сохранения: ' +
                                String((e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail || e),
                            );
                          } finally {
                            setAdminSavingDesc(false);
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold"
                        style={{ background: 'linear-gradient(135deg,#1a6eb5,#0e4a8c)', color: '#fff', border: '1px solid var(--cyan-border)' }}
                      >
                        {adminSavingDesc ? 'Сохранение...' : 'Сохранить'}
                      </button>
                      <button
                        type="button"
                        disabled={adminSavingDesc}
                        onClick={() => {
                          setAdminEditingDesc(false);
                          setAdminDescDraft(
                            groupShortCopy || String(group.short_description || group.short_summary || ''),
                          );
                          haptic.selection();
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                        style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}
                      >
                        Отмена
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {!adminEditingDesc && groupShortCopy && (
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>{groupShortCopy}</p>
            )}
            {isAdmin && !adminEditingDesc && !groupShortCopy && (
              <p className="text-xs italic" style={{ color: 'var(--text-3)' }}>Краткое описание не заполнено.</p>
            )}
            <button
              onClick={() => setShowFullSpecs((s) => !s)}
              className="mt-3 text-xs font-semibold px-3 py-1.5 rounded-xl"
              style={{
                color: 'var(--cyan-bright)',
                background: 'rgba(99,179,237,0.1)',
                border: '1px solid var(--cyan-border)',
              }}
            >
              {showFullSpecs ? 'Скрыть полные ТТХ' : 'Показать полные ТТХ'}
            </button>
          </div>
        )}

        {/* ── FULL SPECS EXPANDED ── */}
        {showFullSpecs && (
          <>
        {(groupFullCopy || isAdmin) && (
          <div className="glass-light px-3.5 py-3.5 sm:px-4 sm:py-4">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--cyan)' }}>◈ Полное описание</p>
            {isAdmin && (
              <div className="mb-2">
                {!adminEditingFullDesc ? (
                  <button
                    type="button"
                    onClick={() => {
                      setAdminFullDescDraft(groupFullCopy);
                      setAdminEditingFullDesc(true);
                      haptic.selection();
                    }}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-lg"
                    style={{ background: 'rgba(99,179,237,0.12)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
                  >
                    ✏️ Редактировать полное описание
                  </button>
                ) : (
                  <div className="rounded-xl p-2.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}>
                    <textarea
                      value={adminFullDescDraft}
                      onChange={(e) => setAdminFullDescDraft(e.target.value)}
                      rows={5}
                      className="w-full rounded-lg p-2 text-xs"
                      style={{ background: 'rgba(10,25,47,0.8)', color: 'var(--text-1)', border: '1px solid var(--border-subtle)' }}
                      placeholder="Введите полное описание..."
                    />
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        disabled={adminSavingFullDesc}
                        onClick={async () => {
                          setAdminSavingFullDesc(true);
                          try {
                            const trimmedFull = adminFullDescDraft.trim();
                            await persistAdminProductGroupCopy({
                              adminSystemId,
                              group,
                              effectiveGroupId,
                              catalogTier,
                              patch: { full_description: trimmedFull },
                            });
                            setGroup((prev) => (prev ? { ...prev, description: trimmedFull } : prev));
                            setAdminFullDescDraft(trimmedFull);
                            setAdminEditingFullDesc(false);
                            await reloadProductGroup({ preserveFull: trimmedFull });
                            haptic.success();
                          } catch (e) {
                            haptic.error();
                            window.alert('Ошибка сохранения: ' + String((e as any)?.response?.data?.detail || (e as any)?.message || e));
                          } finally {
                            setAdminSavingFullDesc(false);
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold"
                        style={{ background: 'linear-gradient(135deg,#1a6eb5,#0e4a8c)', color: '#fff', border: '1px solid var(--cyan-border)' }}
                      >
                        {adminSavingFullDesc ? 'Сохранение...' : 'Сохранить'}
                      </button>
                      <button
                        type="button"
                        disabled={adminSavingFullDesc}
                        onClick={() => {
                          setAdminEditingFullDesc(false);
                          setAdminFullDescDraft(groupFullCopy);
                          haptic.selection();
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                        style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}
                      >
                        Отмена
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {groupFullCopy ? (
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>
                {groupFullCopy}
              </p>
            ) : isAdmin ? (
              <p className="text-xs italic" style={{ color: 'var(--text-3)' }}>Полное описание не заполнено.</p>
            ) : null}
          </div>
        )}

        {/* ── ADVANTAGES ── */}
        {(group.advantages && group.advantages.length > 0) || isAdmin ? (
          <div className="glass-light px-4 py-4">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--cyan)' }}>◈ Преимущества</p>
            {isAdmin && (
              <div className="mb-2">
                {!adminEditingAdvantages ? (
                  <button
                    type="button"
                    onClick={() => {
                      setAdminAdvantagesDraft(Array.isArray(group.advantages) ? group.advantages.join('\n') : '');
                      setAdminEditingAdvantages(true);
                      haptic.selection();
                    }}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-lg"
                    style={{ background: 'rgba(99,179,237,0.12)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
                  >
                    ✏️ Редактировать преимущества
                  </button>
                ) : (
                  <div className="rounded-xl p-2.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}>
                    <textarea
                      value={adminAdvantagesDraft}
                      onChange={(e) => setAdminAdvantagesDraft(e.target.value)}
                      rows={5}
                      className="w-full rounded-lg p-2 text-xs"
                      style={{ background: 'rgba(10,25,47,0.8)', color: 'var(--text-1)', border: '1px solid var(--border-subtle)' }}
                      placeholder="Каждое преимущество с новой строки"
                    />
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        disabled={adminSavingAdvantages}
                        onClick={async () => {
                          const nextAdvantages = adminAdvantagesDraft
                            .split('\n')
                            .map((x) => x.trim())
                            .filter(Boolean);
                          setAdminSavingAdvantages(true);
                          try {
                            await persistAdminProductGroupCopy({
                              adminSystemId,
                              group,
                              effectiveGroupId,
                              catalogTier,
                              patch: { advantages: nextAdvantages },
                            });
                            setGroup((prev) => (prev ? { ...prev, advantages: nextAdvantages } : prev));
                            setAdminEditingAdvantages(false);
                            await reloadProductGroup();
                            haptic.success();
                          } catch (e) {
                            haptic.error();
                            window.alert('Ошибка сохранения: ' + String((e as any)?.response?.data?.detail || (e as any)?.message || e));
                          } finally {
                            setAdminSavingAdvantages(false);
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold"
                        style={{ background: 'linear-gradient(135deg,#1a6eb5,#0e4a8c)', color: '#fff', border: '1px solid var(--cyan-border)' }}
                      >
                        {adminSavingAdvantages ? 'Сохранение...' : 'Сохранить'}
                      </button>
                      <button
                        type="button"
                        disabled={adminSavingAdvantages}
                        onClick={() => {
                          setAdminEditingAdvantages(false);
                          setAdminAdvantagesDraft(Array.isArray(group.advantages) ? group.advantages.join('\n') : '');
                          haptic.selection();
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                        style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}
                      >
                        Отмена
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {group.advantages && group.advantages.length > 0 ? (
              <ul className="space-y-2">
                {group.advantages.map((adv, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <CheckCircle size={14} color="#68D391" className="shrink-0 mt-0.5" />
                    <span className="text-sm leading-snug" style={{ color: 'var(--text-2)' }}>{adv}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs" style={{ color: 'var(--text-3)' }}>Преимущества пока не заполнены.</p>
            )}
          </div>
        ) : null}

        {/* ── CONTROL TYPES ── */}
        {group.control_types && group.control_types.length > 0 && (
          <div className="glass-light px-4 py-4">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--cyan)' }}>◈ Управление</p>
            <p className="text-[10px] mb-3" style={{ color: 'var(--text-3)' }}>Нажмите на опцию — краткое описание</p>
            <div className="grid grid-cols-2 gap-2">
              {group.control_types.map(ct => {
                const icon = Object.entries(CTRL_ICONS).find(([k]) => ct.toLowerCase().includes(k))?.[1] ?? '◽';
                const info = getControlOptionInfo(ct);
                return (
                  <button
                    key={ct}
                    type="button"
                    onClick={() => {
                      setControlInfoEntry(info);
                      haptic.selection();
                    }}
                    className="press flex items-center gap-2 rounded-xl px-3 py-2.5 text-left"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}
                  >
                    <span className="text-lg">{info?.icon ?? icon}</span>
                    <span className="text-xs font-medium leading-tight" style={{ color: 'var(--text-2)' }}>{ct}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── SPECS ── */}
        {hasSpecs && (
          <div className="glass-light px-4 py-4">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--cyan)' }}>◈ Характеристики</p>
            <div>
              {mergedSpecs.map(([key, val], i) => (
                <div key={key} className="flex items-center justify-between py-2.5"
                  style={{ borderBottom: i < mergedSpecs.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                  <span className="text-sm" style={{ color: 'var(--text-3)' }}>{key}</span>
                  <span className="text-sm font-semibold text-right max-w-[55%]" style={{ color: 'var(--text-1)' }}>{String(val)}</span>
                </div>
              ))}
              {activeVariant?.power_watts && !cleanSpecs.find(([k]) => k === 'Мощность') && (
                <div className="flex items-center justify-between py-2.5">
                  <span className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-3)' }}><Zap size={13} /> Мощность</span>
                  <span className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>{activeVariant.power_watts} Вт</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── WORKS WITH ── */}
        <div className="glass-light px-4 py-4">
          <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--cyan)' }}>◈ Совместимость</p>
          <div className="grid grid-cols-2 gap-2">
            {WORKS_WITH_ECOSYSTEMS.map((eco) => (
              <div
                key={eco.id}
                className="flex items-center gap-2 rounded-xl px-3 py-2"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}
              >
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm"
                  style={{ background: 'rgba(99,179,237,0.14)' }}
                >
                  {eco.icon}
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold leading-tight" style={{ color: 'var(--text-1)' }}>{eco.label}</p>
                  {eco.hint ? (
                    <p className="text-[9px] mt-0.5 leading-tight" style={{ color: 'var(--text-3)' }}>{eco.hint}</p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>

          </>
        )}

        {/* ── CONSULTATION CTA ── */}
        <div className="px-4 py-4"
          style={{ background: 'linear-gradient(135deg, rgba(99,179,237,0.15) 0%, rgba(10,25,47,0.8) 100%)', border: '1px solid var(--border-glass)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: 'rgba(99,179,237,0.2)', border: '1px solid var(--cyan-border)' }}>
              <Phone size={18} color="var(--cyan-bright)" />
            </div>
            <div>
              <p className="font-semibold text-sm" style={{ color: 'var(--text-1)' }}>Нужна экспертная консультация?</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-2)' }}>Подберём конфигурацию под интерьер, сценарии и бюджет проекта</p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              onClick={() => {
                haptic.selection();
                window.open(SUPPORT_TG_URL, '_blank', 'noopener,noreferrer');
              }}
              className="press flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold"
              style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-1)', border: '1px solid var(--border-subtle)' }}
            >
              <MessageCircle size={14} /> Связаться с менеджером
            </button>
            <button
              onClick={() => {
                haptic.selection();
                navigate('/measurements?intent=measurement&source=product_group');
              }}
              className="press flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold"
              style={{ background: 'linear-gradient(135deg,#1a6eb5,#0e4a8c)', color: '#fff', border: '1px solid var(--cyan-border)' }}
            >
              <Phone size={14} /> Записаться на профессиональный замер
            </button>
          </div>
        </div>

      </div>

      {/* ── CHECKOUT DOCK (пилюля как bottom-nav, после выбора в конфигураторе) ── */}
      <div
        className={`configurator-checkout-dock px-3 py-2 sm:px-3.5 ${showCheckoutDock ? '' : 'configurator-checkout-dock--hidden'}`}
        aria-hidden={!showCheckoutDock}
      >
        <div className="mb-2 flex flex-wrap gap-1.5">
          {showTierChoice && showCheckoutDock && cfgTouched.tier && selectedTierSlug && (
            <span className={CFG_SUMMARY_CHIP} style={chipAccentStyle(tierAccentKey(selectedTierSlug), true)}>
              {tierPillLabel(selectedTierSlug)}
            </span>
          )}
          {showCheckoutDock && showWiredPowerChip && (
            <span className={CFG_SUMMARY_CHIP} style={chipAccentStyle('power-wired', true)}>
              ⚡ 220В
            </span>
          )}
          {showCheckoutDock && cfgTouched.power && selectedBattery === true && (
            <span className={CFG_SUMMARY_CHIP} style={chipAccentStyle('power-battery', true)}>
              🔋 АКБ
            </span>
          )}
          {showCheckoutDock && cfgTouched.protocol && selectedProtocol && (
            <span className={CFG_SUMMARY_CHIP} style={chipStyleForPillLabel(selectedProtocol)}>{selectedProtocol}</span>
          )}
          {showCheckoutDock && selectedControlTags.map((tag) => (
            <span key={`pill-${tag}`} className={CFG_SUMMARY_CHIP} style={chipStyleForPillLabel(tag)}>{tag}</span>
          ))}
          {showCheckoutDock && cfgTouched.profile_color && selectedProfileColor && (
            <span className={CFG_SUMMARY_CHIP} style={chipAccentStyle(colorAccentKey(selectedProfileColor), true)}>{selectedProfileColor}</span>
          )}
          {showCheckoutDock && cfgTouched.motor_color && selectedMotorColor
            && (!selectedProfileColor || selectedMotorColor.trim().toLowerCase() !== selectedProfileColor.trim().toLowerCase()) && (
            <span className={CFG_SUMMARY_CHIP} style={chipAccentStyle(colorAccentKey(selectedMotorColor), true)}>
              {selectedMotorColor}
            </span>
          )}
          {showCheckoutDock && !splitColorSteps && cfgTouched.color && selectedColor && (
            <span className={CFG_SUMMARY_CHIP} style={chipAccentStyle(colorAccentKey(selectedColor), true)}>{selectedColor}</span>
          )}
          {showCheckoutDock && selectedDim !== null && dimKey && (
            <span className={CFG_SUMMARY_CHIP} style={chipAccentStyle('size', true)}>
              {matrixLayout.mode === '2d' && selectedSecondDim != null
                ? `${selectedDim}×${selectedSecondDim} м`
                : `${selectedDim} м`}
              {roundedSelectedDim !== null && roundedSelectedDim !== selectedDim ? ` → ${roundedSelectedDim} м` : ''}
            </span>
          )}
          {showCheckoutDock && cfgTouched.opening && selectedOpening && (
            <span className={CFG_SUMMARY_CHIP} style={chipAccentStyle('opening', true)}>
              {selectedOpening === 'center' ? 'К центру' : selectedOpening === 'left' ? 'Влево' : 'Вправо'}
            </span>
          )}
          {showCheckoutDock && (() => {
            const ch = parseClientHeightM(clientHeightM);
            return ch != null ? (
              <span className={CFG_SUMMARY_CHIP} style={{ background: 'rgba(129,199,132,0.14)', color: '#B7F5CB', border: '1px solid rgba(129,199,132,0.35)' }}>
                ↕ {ch} м
              </span>
            ) : null;
          })()}
        </div>
        <div className="flex items-center gap-3">
          <div className="shrink-0 min-w-[72px]">
            <p className="text-[9px] uppercase tracking-widest font-semibold mb-0.5" style={{ color: 'var(--text-3)' }}>
              {hasPartnerDiscount ? 'Ваша цена' : (displayPrice !== minPrice ? 'Итого' : 'от')}
            </p>
            <p
              className="text-lg sm:text-xl font-black tabular-nums leading-tight"
              style={{
                color: 'var(--cyan-bright)',
                ...premiumPriceRevealStyle,
              }}
            >
              {Math.round(animatedCheckoutPrice).toLocaleString('ru-RU')}
              <span className="text-sm font-semibold ml-0.5" style={{ color: 'var(--text-2)' }}>₽</span>
            </p>
            {hasPartnerDiscount && savingsPrice > 0 && (
              <p className="text-[9px] mt-0.5 leading-tight" style={{ color: '#9AE6B4' }}>
                РРЦ {Math.round(retailPrice).toLocaleString('ru-RU')} ₽ · −{effectiveDiscountPct.toFixed(0)}% · выгода {Math.round(savingsPrice).toLocaleString('ru-RU')} ₽
              </p>
            )}
          </div>
          <button
            onClick={handleAddToCart}
            disabled={adding}
            className="press flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold text-sm transition-all"
            style={added
              ? { background: 'rgba(72,187,120,0.25)', color: '#68D391', border: '1px solid rgba(72,187,120,0.4)', boxShadow: '0 0 20px rgba(72,187,120,0.2)' }
              : !canAddToCart
              ? { background: 'rgba(255,255,255,0.05)', color: 'var(--text-3)', border: '1px solid var(--border-subtle)' }
              : { background: 'linear-gradient(135deg,#1a6eb5,#0e4a8c)', color: '#fff', border: '1px solid var(--cyan-border)', boxShadow: '0 0 24px rgba(99,179,237,0.3)' }
            }
          >
            {added ? (
              <><CheckCircle size={18} /> Добавлено в проект</>
            ) : adding ? (
              <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: 'rgba(255,255,255,0.5)', borderTopColor: 'transparent' }} />
            ) : needsSize ? (
              <><Ruler size={18} /> Выберите размер</>
            ) : (
              <><ShoppingCart size={18} /> В корзину</>
            )}
          </button>
        </div>
      </div>

      <ControlInfoSheet entry={controlInfoEntry} onClose={() => setControlInfoEntry(null)} />

    </div>
  );
}
